import get from 'lodash-es/get.js';
import isEqual from 'lodash-es/isEqual.js';
import groupBy from 'lodash-es/groupBy.js';
import omit from 'lodash-es/omit.js';
import { createSelector, lruMemoize } from 'reselect';
import { buildContextKey, isQuickActionAllowed } from './utils.js';
import * as app from '../app';
import * as auth from '../auth';
import * as board from '../board';
import * as boardUtils from '../board/utils.js';
import * as boardHighlightsSelectors from '../board-highlights/selectors.js';
import * as feature from '../feature';
import * as router from '../router';
import * as manageAccount from '../manage-account';
import * as user from '../user';
// Direct path — card-translate/index.js pulls in its saga.
import * as firestoreRedux from '@dreamworld/firestore-redux';
// The selector modules rather than the redux modules: importing `../chat` or `../card-details` would
// run their sagas and add their reducers wherever the AI chat is imported. The selectors read through
// `get`, so they answer safely before either slice exists.
import * as chatSelectors from '../chat/selectors.js';
import * as cardDetailsSelectors from '../card-details/selectors.js';

/** Stable empty result, so a connected input does not re-render on every dispatch. */
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

/**
 * Returns false if the current BOARD page's per-type feature flag disables AI.
 * Returns true for all non-BOARD pages.
 * @param {Object} state
 * @returns {Boolean}
 */
const _isBoardAvailable = (state) => {
  if (router.selectors.pageName(state) !== 'BOARD') return true;
  const boardId = router.selectors.pageBoardId(state);
  const isUseCase = board.selectors.isUseCaseBoard(state, boardId);
  return isUseCase
    ? feature.selectors.isAvailable(state, 'ai-chat-use-case-board')
    : feature.selectors.isAvailable(state, 'ai-chat-other-boards');
};

/**
 * Returns false if AI is disabled at the account level.
 * If router has an accountId, checks that account only.
 * Otherwise (Home), returns true if any accessible account has AI enabled.
 * @param {Object} state
 * @returns {Boolean}
 */
const _isAvailableForAccount = (state) => {
  const accountId = router.selectors.accountId(state);
  if (accountId) {
    return !manageAccount.selectors.aiDisabled({ state, accountId });
  }
  const accounts = auth.selectors.accessibleAccounts(state);
  if (!accounts.length) return true;
  return accounts.some(id => !manageAccount.selectors.aiDisabled({ state, accountId: id }));
};

/**
 * Returns whether AI Chat feature is available for the current user and page context.
 *
 * AI Chat is available when:
 * - User is logged in (has a userId)
 * - AND feature flag 'ai-chat' is enabled
 * - AND board-level check passes (per-type sub-flag for BOARD pages)
 * - AND account-level check passes (aiDisabled not set for the account)
 *
 * @param {Object} state - Redux state
 * @returns {Boolean} true if AI Chat is available for the user in the current context
 */
export const isAvailable = (state) => {
  if (!auth.selectors.currentUserId(state)) return false;
  if (!feature.selectors.isAvailable(state, 'ai-chat')) return false;
  if (!_isBoardAvailable(state)) return false;
  return _isAvailableForAccount(state);
};

/**
 * Returns the query ID used for the recent chat prefetch query based on context.
 *
 * @param {String} contextType - Context type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * @param {String} boardId - Board ID (for BOARD context)
 * @param {String} cardId - Card ID (for CARD context)
 * @returns {String} Query ID
 */
export const recentChatQueryId = (contextType, boardId, cardId) => {
  switch (contextType) {
    case 'CARD':
      return `recent-card-chat-${cardId}`;
    case 'BOARD':
      return `recent-board-chat-${boardId}`;
    case 'HOME':
    case 'ONBOARDING':
    default:
      return 'recent-user-chat';
  }
};

/**
 * Returns the most recent chat in the given context if within the recency threshold.
 *
 * Reads from prefetched Firestore query result (dispatched by prefetchRecentChatSaga on page load).
 * Checks if `latestMessageSentAt` is within the configured recency threshold (default: 24 hours).
 *
 * @param {Object} state - Redux state
 * @param {String} contextType - Context type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * @param {String} boardId - Board ID (for BOARD context)
 * @param {String} cardId - Card ID (for CARD context)
 * @returns {Object|null} Chat document or null if no recent chat exists
 */
export const recentChatInContext = createSelector(
  (state, contextType, boardId, cardId) => firestoreRedux.selectors.docsByQueryResult(state, recentChatQueryId(contextType, boardId, cardId), 'ai-chats'),
  (state) => app.selectors.config(state),
  (chats, config) => {
    if (!chats || chats.length === 0) return null;

    const chat = chats[0];
    if (!chat || !chat.latestMessageSentAt) return null;

    // Check recency threshold
    const thresholdHours = get(config, 'aiChat.recencyThresoldHours', 24);
    const now = Date.now();
    const lastMessageTime = chat.latestMessageSentAt;
    const diffHours = (now - lastMessageTime) / (1000 * 60 * 60);

    return diffHours <= thresholdHours ? chat : null;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * Returns all chat documents for the current context.
 *
 * Reads from the prefetched Firestore query result (dispatched by prefetchRecentChatSaga).
 * Unlike `recentChatInContext`, this returns all chats without recency threshold filtering.
 * Results are ordered by latestMessageSentAt descending.
 *
 * @param {Object} state - Redux state
 * @returns {Array} Array of chat documents, empty array if none
 */
export const userChats = createSelector(
  (state) => {
    const context = currentContext(state);
    if (!context) return [];
    return firestoreRedux.selectors.docsByQueryResult(state, recentChatQueryId(context.type, context.boardId, context.cardId), 'ai-chats') || [];
  },
  (chats) => {
    if (!chats || chats.length === 0) return [];
    return [...chats].sort((a, b) => {
      const aTime = a.latestMessageSentAt || 0;
      const bTime = b.latestMessageSentAt || 0;
      return bTime - aTime;
    });
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * Returns whether at least one chat exists in the given context.
 *
 * Used by `ai-chat-actions-header` to determine visibility of the Chat History button.
 * Button is shown only when this returns true.
 *
 * @param {Object} state - Redux state
 * @param {String} contextType - Context type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * @param {String} boardId - Board ID (for BOARD context)
 * @param {String} cardId - Card ID (for CARD context)
 * @returns {Boolean} true if at least one chat exists in context
 */
export const hasChatInContext = (state) => {
  return userChats(state).length > 0;
};

/**
 * Returns the current page context for AI chat prefetching.
 *
 * This selector determines context based on the current page/dialog state,
 * regardless of whether AI chat is open. Used for prefetching recent chats
 * and quick actions.
 *
 * Context structure:
 * - type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * - boardId: ID of the current board (for BOARD/CARD contexts)
 * - cardId: ID of the current card (only for CARD context)
 *
 * @param {Object} state - Redux state
 * @returns {Object|null} Context object { type, boardId?, cardId? } or null if no valid context
 */
const basePageContext = createSelector(
  (state) => router.selectors.pageName(state),
  (state) => router.selectors.pageBoardId(state),
  (state) => router.selectors.dialogName(state),
  (state) => router.selectors.cardId(state),
  (pageName, boardId, dialogName, cardId) => {
    if (dialogName === 'CARD' && cardId) {
      return { type: 'CARD', boardId, cardId };
    }
    if (pageName === 'BOARD' && boardId) {
      return { type: 'BOARD', boardId };
    }
    if (pageName === 'BOARD_EXPLORER') {
      return { type: 'HOME' };
    }
    if (pageName === 'SIGNUP') {
      return { type: 'ONBOARDING' };
    }
    return null;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * The unsent edit of the message open for editing, when that message belongs to this card or board.
 *
 * @param {Object} state Redux state.
 * @param {String} boardId Board the thread belongs to.
 * @param {String} cardId Card the thread belongs to, absent for a board chat.
 * @returns {Object|null} `{ chatMessageId, text }`, or null when nothing is being edited here.
 */
const openChatEdit = (state, boardId, cardId) => {
  const chatMessageId = chatSelectors.openEditId(state);
  const text = chatMessageId && chatSelectors.editDraft(state, chatMessageId);
  if (!text) {
    return null;
  }

  const message = chatMessageId.startsWith('ccm_')
    ? chatSelectors.cardChatItem(state, chatMessageId)
    : chatSelectors.boardChatItem(state, chatMessageId);
  const belongsHere = message && (cardId ? message.cardId === cardId : (message.boardId === boardId && !message.cardId));
  return belongsHere ? { chatMessageId, text } : null;
};

/**
 * What the user has written on this page and not saved: the description under the card, and either an
 * unsent edit of a posted message or what is sitting in the chat box.
 *
 * An open edit wins over the chat box when both hold something, because only one message can be open
 * for editing at a time and that editor is the one the user is looking at. `chatMessageId` goes with
 * it, so the assistant is told which of the two it has rather than inferring it from the text.
 *
 * @param {Object} state Redux state.
 * @param {Object} context The page context, for its board and card.
 * @returns {Object|null} `{ description?, chatMessage? }`, or null when nothing is unsaved here.
 */
const pageDraft = (state, context) => {
  const { boardId, cardId } = context;
  const containerId = cardId || boardId;
  if (!containerId) {
    return null;
  }

  const description = cardId ? cardDetailsSelectors.descriptionDraft(state, cardId) : '';
  const edit = openChatEdit(state, boardId, cardId);
  const newChatMessage = chatSelectors.newChatDraft(state, containerId);
  const chatMessage = edit
    ? { text: edit.text, chatMessageId: edit.chatMessageId }
    : (newChatMessage ? { text: newChatMessage } : null);

  if (!description && !chatMessage) {
    return null;
  }

  return {
    ...(description && { description }),
    ...(chatMessage && { chatMessage }),
  };
};

/**
 * The page context with whatever the user has written here and not saved.
 *
 * `draft` is what lets the AI polish text the server has never seen.
 */
export const pageContext = createSelector(
  (state) => basePageContext(state),
  (state) => {
    const context = basePageContext(state);
    return context ? pageDraft(state, context) : null;
  },
  (context, draft) => {
    if (!context) {
      return null;
    }

    return { ...context, ...(draft && { draft }) };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * Returns current AI Chat context with type, boardId, and optional cardId.
 *
 * Context structure:
 * - type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * - boardId: ID of the current board (null for HOME/ONBOARDING)
 * - cardId: ID of the current card (only present when type is 'CARD')
 *
 * Reads from router state which pre-computes the context:
 * - If card dialog is open with AI chat → CARD context
 * - If page AI chat is open → BOARD/HOME/ONBOARDING context
 *
 * @param {Object} state - Redux state
 * @returns {Object|null} Context object { type, boardId?, cardId? } or null if closed
 */
export const currentContext = (state) => {
  return router.selectors.currentAiChatContext(state);
};

/**
 * Returns AI chat panel width for given context.
 *
 * Default widths:
 * - BOARD context: 450px
 * - CARD context: 450px
 *
 * Width is persisted separately for each context and restored on page reload.
 *
 * @param {Object} state - Redux state
 * @param {String} context - Context type: 'BOARD' or 'CARD'
 * @returns {Number} Panel width in pixels
 */
export const panelWidth = (state, context) => {
  if (context === 'CARD') {
    // CARD: constraints enforced at drag site; selector is a pure read.
    return get(state, 'ai-chat.panelWidths.card', 450);
  }
  
  // BOARD and HOME are floating sidebars — apply the 60% viewport cap here
  // to protect against stale values from wider screens.
  const key = context === 'HOME' ? 'home' : context === 'ONBOARDING' ? 'onboarding' : 'board';
  const stored = get(state, `ai-chat.panelWidths.${key}`, 450);
  const viewportWidth = get(state, 'app.viewportWidth', window.innerWidth);
  return Math.max(420, Math.min(viewportWidth * 0.6, stored));
};

/**
 * Returns the card detail content width.
 *
 * This is the width of the card detail content area and is constant regardless
 * of AI chat state. When AI chat is open, the host dialog expands by the AI chat
 * panel width — the card content area itself never changes.
 *
 * Default width: 800px
 *
 * Migration: the previous architecture stored the combined dialog+chat total width under
 * 'panelWidths.cardDetail'. When no 'cardContent' value exists, the old value is recovered
 * by subtracting the stored AI chat panel width.
 *
 * @param {Object} state - Redux state
 * @returns {Number} Card detail content width in pixels
 */
export const cardDetailWidth = (state) => {
  const cardContent = get(state, 'ai-chat.panelWidths.cardContent', null);
  if (cardContent !== null) {
    return cardContent;
  }
  // Migrate from previous architecture: 'cardDetail' stored the combined (card + chat) width.
  const oldCombined = get(state, 'ai-chat.panelWidths.cardDetail', null);
  if (oldCombined !== null) {
    const chatWidth = get(state, 'ai-chat.panelWidths.card', 480);
    return Math.max(680, oldCombined - chatWidth);
  }
  return 800;
};

// Default FAB positions by context
const DEFAULT_FAB_POSITIONS = {
  BOARD: { x: 20, y: 20 },
  CARD: { x: 60, y: 20 }, // top-anchored: y is measured from the top edge
  HOME: { x: 20, y: 20 },
  ONBOARDING: { x: 20, y: 20 },
};

// FAB position keys mapping
const FAB_POSITION_KEYS = {
  BOARD: 'board',
  CARD: 'card',
  HOME: 'home',
  ONBOARDING: 'onboarding',
};

/**
 * Returns FAB (Floating Action Button) position for given context.
 *
 * Default positions:
 * - BOARD context: { x: 20, y: 20 }
 * - CARD context (fixed, not draggable): { x: 64, y: 20 } on wide layout, beside the close button;
 *   { x: 8, y: 8 } on narrow, at the card page header's right edge
 * - HOME context: { x: 20, y: 20 }
 * - ONBOARDING context: { x: 20, y: 20 }
 *
 * `x` is always an offset from the right edge. `y` is an offset from the top
 * edge for CARD context (top-anchored) and from the bottom edge otherwise.
 * Position is persisted separately for each context and restored on page reload.
 * Position is relative to the container element, not viewport.
 * Uses createSelector for memoization since it returns an object.
 *
 * @param {Object} state - Redux state
 * @param {String} context - Context type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * @returns {Object} Position object { x: Number, y: Number } in pixels
 */
export const fabPosition = createSelector(
  (state) => get(state, 'ai-chat.fabPositions') || {},
  (state) => app.selectors.wideLayout(state),
  (state, context) => context,
  (fabPositions, wideLayout, context) => {
    const key = FAB_POSITION_KEYS[context] || 'board';
    const defaultPosition = DEFAULT_FAB_POSITIONS[context] || DEFAULT_FAB_POSITIONS.BOARD;
    const storedPosition = fabPositions[key];

    // The card FAB is fixed in the header row (never dragged), so a stored position is ignored.
    // Narrow: the card page header's right edge; translate sits left of the FAB.
    if (context === 'CARD' && !wideLayout) {
      return { x: 8, y: 8 };
    }

    // Wide: the slot beside the 40px close button, 8px apart; translate sits left of the FAB.
    if (context === 'CARD') {
      return { x: 64, y: 20 };
    }

    return storedPosition || defaultPosition;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);


/**
 * Returns whether the current chat was opened from a quick action.
 *
 * When true, certain UI elements (e.g., new conversation education tip)
 * should be suppressed since the user already understands the chat flow.
 *
 * @param {Object} state - Redux state
 * @returns {Boolean} true if the current chat was opened via a quick action
 */
export const isOpenedFromQuickAction = (state) => get(state, 'ai-chat.openedFromQuickAction', false);


/**
 * Returns the initial message of a chat (the first message with no previous message).
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {Object|null} The initial message or null
 */
const initialMessage = createSelector(
  (state, chatId) => messages(state, chatId),
  (msgs) => msgs?.find(m => m.initial || !m.previousMessageId) ?? null,
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns whether a chat was started via a quick action.
 *
 * Checks if the initial message has quickAction data in its request.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {Boolean} true if the chat was started via a quick action
 */
export const isQuickActionChat = createSelector(
  (state, chatId) => initialMessage(state, chatId),
  (initMsg) => !!initMsg?.request?.quickAction
);

/**
 * Returns whether user is currently resizing panels.
 *
 * This state is used to:
 * - Disable CSS transitions during resize for better performance
 * - Show appropriate resize cursors
 * - Prevent other interactions during resize
 *
 * @param {Object} state - Redux state
 * @returns {Boolean} true if resize operation is in progress
 */
export const isResizing = (state) => get(state, 'ai-chat.isResizing', false);

/**
 * The AI context rows the `@` picker shows above its results in a board chat, as `{ id }`:
 * SELECTED with cards selected (carrying them, for its label's count), HIGHLIGHTED with a highlight on,
 * VISIBLE_COLUMNS with a column hidden. Empty when the picker was not opened by a board chat, or with
 * `ai-chat-implicit-context` off.
 */
export const referenceContextRows = createSelector(
  (state) => get(state, 'references.search.contextKey'),
  (state) => get(state, 'references.search.boardId'),
  (state) => feature.selectors.isAvailable(state, 'ai-chat-implicit-context'),
  (state) => board.selectors.selectedCards(state),
  (state) => !!boardHighlightsSelectors.highlightCriteria(state),
  (state) => !!board.selectors.hiddenColumnIds(state)?.length,
  (contextKey, boardId, isEnabled, selectedCardIds, hasHighlight, hasHiddenColumn) => {
    if (!isEnabled || !boardId || contextKey !== buildContextKey('BOARD', boardId)) {
      return EMPTY_ARRAY;
    }
    return [
      selectedCardIds?.length && { id: 'SELECTED', selectedCardIds },
      hasHighlight && { id: 'HIGHLIGHTED' },
      hasHiddenColumn && { id: 'VISIBLE_COLUMNS' },
    ].filter(Boolean);
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

/**
 * Returns the current chat ID from router state.
 *
 * Determines the "current" chat based on focused context:
 * - If dialog AI chat is open → returns dialog chat ID
 * - Else if page AI chat is open → returns page chat ID
 *
 * @param {Object} state - Redux state
 * @returns {String|null} Chat ID or null if no chat is active
 */
export const currentChatId = (state) => {
  const dialogChat = router.selectors.dialogAiChat(state);
  const pageChat = router.selectors.pageAiChat(state);
  return dialogChat.isOpened ? dialogChat.chatId : (pageChat.isOpened ? pageChat.chatId : null);
};


/**
 * Returns a chat document from Firestore by chatId.
 *
 * Chat document structure:
 * - chatId: Unique chat identifier
 * - userId: Owner of the chat
 * - title: Auto-generated chat title
 * - context: { type, boardId, cardId? }
 * - shared: Boolean - whether chat is shared with team
 * - latestMessageSentAt: Timestamp of last message
 * - createdAt: Chat creation timestamp
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {Object|null} Chat document or null if not found
 */
export const chat = (state, chatId) => {
  if (!chatId) return null;
  return firestoreRedux.selectors.doc(state, 'ai-chats', chatId);
};

/**
 * Returns true if the chat failed to load due to a permission denied error.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {Boolean}
 */
export const isChatAccessDenied = (state, chatId) => {
  if (!chatId) return false;
  return !!get(state, `ai-chat.accessDeniedChats.${chatId}`, false);
};

/**
 * Returns the current chat document from Firestore.
 * Uses createSelector for memoization.
 *
 * @param {Object} state - Redux state
 * @returns {Object|null} Chat document or null if no chat is active
 */
export const currentChat = createSelector(
  (state) => currentChatId(state),
  (state) => state,
  (chatId, state) => {
    if (!chatId) return null;
    return chat(state, chatId);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns the current chat's title.
 * The title is auto-generated based on the initial message sent by the user.
 * Uses createSelector for memoization.
 *
 * @param {Object} state - Redux state
 * @returns {String|null} Chat title or null if no chat is active or title not yet generated
 */
export const currentChatTitle = createSelector(
  (state) => currentChat(state),
  (chat) => get(chat, 'title', null),
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns the user ID of the chat owner (the user who created the chat).
 * This is needed to display the correct user name in page titles for shared chats.
 * Uses createSelector for memoization.
 *
 * @param {Object} state - Redux state
 * @returns {String|null} User ID or null if no chat is active
 */
export const currentChatOwnerId = createSelector(
  (state) => currentChat(state),
  (chat) => get(chat, 'userId', null),
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns the first name of the chat owner (the user who created the chat).
 * Reads from the user document in Firestore using the chat's userId field.
 * Uses createSelector for memoization.
 *
 * @param {Object} state - Redux state
 * @returns {String|null} User's first name or null if not available
 */
export const currentChatOwnerFirstName = createSelector(
  (state) => currentChatOwnerId(state),
  (state) => state,
  (ownerId, state) => {
    if (!ownerId) return null;
    const user = firestoreRedux.selectors.doc(state, 'users', ownerId);
    return get(user, 'firstName', null);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns all messages for a given chat, filtered and ordered for display.
 *
 * Messages are stored in Firestore as a subcollection:
 * - Path: ai-chats/{chatId}/ai-messages/{messageId}
 * - Loaded via: firestoreRedux.query(`ai-chats/${chatId}/ai-messages`)
 * - Query ID: `ai-messages-${chatId}`
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {Array} Array of AiMessage objects in display order (oldest to newest)
 */
const allMessages = (state, chatId) => {
  if (!chatId) return [];

  // Get messages from Firestore query result
  // The query ID is `ai-messages-${chatId}` (set in loadChatDataSaga)
  return firestoreRedux.selectors.docsByQueryResult(
    state,
    `ai-messages-${chatId}`,
    `ai-messages`
  );
};

/**
 * Returns whether a cancel request has been initiated for the given message.
 * Used to prevent cancel button from reappearing when Firestore snapshots
 * temporarily reset message status to GENERATING after a cancel was requested.
 *
 * @param {Object} state - Redux state
 * @param {String} messageId - Message ID
 * @returns {Boolean} true if cancel has been requested for this message
 */
export const messages = createSelector(
  allMessages,
  (state) => get(state, 'ai-chat.cancelMessages', {}),
  (allMessages, cancelMessages) => {
    if (!allMessages || allMessages.length === 0) return [];

    // Only show messages that have not been replaced by an edit
    const filteredMessages = allMessages.filter(msg => !msg.editedToMessageId);

    // Sort using previousMessageId linked-list chain
    const nextMap = new Map(); // previousMessageId -> message
    filteredMessages.forEach(m => {
      if (m.previousMessageId) nextMap.set(m.previousMessageId, m);
    });
    const root = filteredMessages.find(m => !m.previousMessageId || m.initial);
    const orderedMessages = [];
    let current = root;
    while (current) {
      orderedMessages.push(current);
      current = nextMap.get(current.id);
    }
    // Append any orphaned messages not reachable from root (fallback)
    if (orderedMessages.length < filteredMessages.length) {
      const includedIds = new Set(orderedMessages.map(m => m.id));
      filteredMessages
        .filter(m => !includedIds.has(m.id))
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
        .forEach(m => orderedMessages.push(m));
    }

    // Sort responses and add isCancelled/lastMessage fields based on status and cancelMessages
    const lastIndex = orderedMessages.length - 1;
    return orderedMessages.map((msg, index) => {
      const lastMessage = index === lastIndex;
      if (!msg.responses || msg.responses.length === 0) {
        return { ...msg, lastMessage };
      }

      const sortedResponses = [...msg.responses]
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
        .map((response) => {
          const status = response.status || msg.status;
          return {
            ...response,
            status,
            statusAt: response.statusAt || msg.statusAt,
            error: response.status === 'ERROR' || msg.status === 'ERROR' ? (response.error ?? msg.error) : undefined,
            isCancelled: status === 'CANCELLED' || (status !== 'COMPLETED' && status !== 'ERROR' && !!cancelMessages[msg.id]),
          };
        });
      return { ...msg, responses: sortedResponses, lastMessage };
    });
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns a single message by ID from a chat.
 *
 * This is a factory function that creates a memoized selector for a specific message.
 * Uses createSelector to avoid recomputation when messages haven't changed.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} messageId - Message ID
 * @returns {Object|null} Message object or null if not found
 */
export const message = createSelector(
  (state, chatId) => messages(state, chatId),
  (state, chatId, messageId) => messageId,
  (allMessages, messageId) => {
    if (!allMessages || allMessages.length === 0 || !messageId) {
      return null;
    }
    return allMessages.find(m => m.id === messageId) || null;
  }
);

/**
 * Returns resolved card data for SELECTED/HIGHLIGHTED implicit context types.
 * Returns null for all other context types.
 *
 * boardId falls back to: chat.context.boardId → router.pageBoardId
 *
 * Each entry is { id, title, url }. Cards not yet in Redux state are omitted.
 *
 * resultEqualityCheck prevents re-renders when array contents haven't changed.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} messageId - Message ID
 * @returns {Array|null}
 */
export const implicitContextCards = createSelector(
  (state, chatId, messageId) => message(state, chatId, messageId),
  (state, chatId) => firestoreRedux.selectors.doc(state, 'ai-chats', chatId),
  (state) => router.selectors.pageBoardId(state),
  (state) => router.selectors.accountId(state),
  (state) => state,
  (msg, chatDoc, pageBoardId, accountId, state) => {
    const context = msg?.request?.context;
    const type = context?.type;
    if (type !== 'SELECTED' && type !== 'HIGHLIGHTED') return null;

    const boardId = chatDoc?.context?.boardId || pageBoardId;
    const cardIds = type === 'SELECTED' ? (context.selectedCardIds || []) : (context.highlightedCardIds || []);
    return cardIds.reduce((acc, cardId) => {
      const card = firestoreRedux.selectors.doc(state, 'cards', cardId);
      if (card) acc.push({ id: cardId, title: card.title, url: `/${accountId}/board/${boardId}/${cardId}` });
      return acc;
    }, []);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  }
);

/**
 * Returns messages for the current chat, filtered and ordered for display.
 *
 * This is a convenience selector that delegates to the `messages` selector
 * with the current chat ID. All message filtering and ordering logic is now
 * handled by the `messages` selector.
 *
 * @param {Object} state - Redux state
 * @returns {Array} Array of AiMessage objects in display order (oldest to newest)
 */
export const currentChatMessages = (state) => {
  return messages(state, currentChatId(state));
};

/**
 * Returns the last message in the current chat conversation.
 * This is the most recent message in the active message chain.
 * Uses createSelector for memoization.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Optional chat ID. If not provided, uses currentChatId
 * @returns {Object|null} Last AiMessage or null if no messages exist
 */
export const lastMessage = createSelector(
  (state, chatId) => chatId, // Input selector 1: the provided chatId
  currentChatId,             // Input selector 2: the currentChatId
  (state, chatId) => messages(state, chatId || currentChatId(state)), // Input selector 3: the resolved messages list
  (providedChatId, resolvedCurrentChatId, allResolvedMessages) => {
    const targetChatId = providedChatId || resolvedCurrentChatId;
    if (!targetChatId) return null;

    // The messages are already resolved and ordered by the third input selector.
    // The last element is always the tail of the chain.
    if (!allResolvedMessages || allResolvedMessages.length === 0) return null;

    return allResolvedMessages[allResolvedMessages.length - 1];
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns whether the specified chat has a message currently being generated.
 * Uses createSelector for memoization.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Optional chat ID. If not provided, uses currentChatId
 * @returns {Boolean} true if a message is in progress (status = 'GENERATING')
 */
export const hasInProgressMessage = createSelector(
  (state, chatId) => messages(state, chatId || currentChatId(state)),
  (allResolvedMessages) => {
    if (!allResolvedMessages || allResolvedMessages.length === 0) return false;
    return allResolvedMessages.some(msg => {
      if (!msg || msg.status !== 'GENERATING') return false;
      if (!msg.responses || msg.responses.length === 0) return true;
      return msg.responses.some(r => !r.isCancelled);
    });
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns the ID of the message currently being edited for a specific chat.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {String|null} Message ID being edited or null
 */
export const editMessage = (state, chatId) => {
  if (!chatId) return null;
  return get(state, `ai-chat.editMessages.${chatId}`, null);
};

/**
 * The in-progress edit text for a chat's currently-edited message (persisted so it survives a
 * panel close/reopen), or null.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {String|null}
 */
export const editDraft = (state, chatId) => {
  if (!chatId) return null;
  return get(state, `ai-chat.editDrafts.${chatId}`, null);
};

/**
 * Determines AI Chat layout mode based on device type, orientation, context, and viewport.
 *
 * Layout Modes:
 * - SIDE_PANEL: Full-height panel rendered directly in page (desktop, tablet landscape, tablet portrait card >820px)
 * - BOTTOM_SHEET: Modal bottom sheet dialog (mobile, tablet portrait board, tablet portrait card ≤820px)
 * - null: AI chat is closed
 *
 * Decision Matrix:
 *
 * | Device    | Orientation | Context | Viewport | Mode         |
 * |-----------|-------------|---------|----------|--------------|
 * | Desktop   | Any         | Board   | Any      | SIDE_PANEL   |
 * | Desktop   | Any         | Card    | Any      | SIDE_PANEL   |
 * | Tablet    | Landscape   | Board   | Any      | SIDE_PANEL   |
 * | Tablet    | Landscape   | Card    | Any      | SIDE_PANEL   |
 * | Tablet    | Portrait    | Board   | ≤820px   | BOTTOM_SHEET |
 * | Tablet    | Portrait    | Board   | >820px   | BOTTOM_SHEET |
 * | Tablet    | Portrait    | Card    | ≤820px   | BOTTOM_SHEET |
 * | Tablet    | Portrait    | Card    | >820px   | SIDE_PANEL   |
 * | Mobile    | Portrait    | Board   | Any      | BOTTOM_SHEET |
 * | Mobile    | Portrait    | Card    | Any      | BOTTOM_SHEET |
 *
 * Component Rendering:
 * - SIDE_PANEL mode:
 *   - kerika-board-page: Renders ai-chat-panel directly with full height
 *   - card-detail-desktop-dialog: Renders ai-chat-panel in split-view with card detail
 * - BOTTOM_SHEET mode:
 *   - kerika-board-page: Renders ai-chat-dialog with placement="bottom"
 *   - kerika-board-card-page: Renders ai-chat-dialog with placement="bottom"
 *
 * @param {Object} state - Redux state
 * @returns {String|null} 'SIDE_PANEL', 'BOTTOM_SHEET', or null if chat is closed
 */
export const layoutMode = createSelector(
  (state) => router.selectors.isPageAiChatOpen(state),
  (state) => router.selectors.isDialogAiChatOpen(state),
  (state) => currentContext(state),
  (state) => app.selectors.isDesktopDevice(state),
  (state) => app.selectors.isTabletDevice(state),
  (state) => app.selectors.isMobileDevice(state),
  (state) => app.selectors.isLandscape(state),
  (state) => app.selectors.isPortrait(state),
  (state) => get(state, 'app.viewportWidth', 0),
  (isPageAiChatOpen, isDialogAiChatOpen, context, isDesktop, isTablet, isMobile, isLandscape, isPortrait, viewportWidth) => {
    const contextType = context?.type; // 'BOARD' or 'CARD'
    const isOpen = contextType === 'CARD' ? isDialogAiChatOpen : isPageAiChatOpen;
    // If chat is closed or no context, no layout mode
    if (!isOpen || !contextType) {
      return null;
    }

    // Desktop: Always SIDE_PANEL (full-height right panel or split-view)
    if (isDesktop) {
      return 'SIDE_PANEL';
    }

    // Tablet Landscape: Always SIDE_PANEL (enough horizontal space)
    if (isTablet && isLandscape) {
      return 'SIDE_PANEL';
    }

    // Tablet Portrait
    if (isTablet && isPortrait) {
      // Board context: Always BOTTOM_SHEET (preserve vertical board space)
      if (contextType === 'BOARD') {
        return 'BOTTOM_SHEET';
      }

      // Card context: Depends on viewport width
      // >820px: SIDE_PANEL (enough space for split-view)
      // ≤820px: BOTTOM_SHEET (preserve card detail space)
      if (contextType === 'CARD') {
        return viewportWidth > 820 ? 'SIDE_PANEL' : 'BOTTOM_SHEET';
      }
    }

    // Mobile: Always BOTTOM_SHEET (limited screen space)
    if (isMobile) {
      return 'BOTTOM_SHEET';
    }

    // Fallback: Default to BOTTOM_SHEET for safety
    return 'BOTTOM_SHEET';
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns whether this is a new chat session.
 * Reads from router state (isNewChat flag from URL param `aichat=new`).
 *
 * @param {Object} state - Redux state
 * @returns {Boolean} true if starting a new chat session
 */
export const isNewChatSession = (state) => {
  const dialogChat = router.selectors.dialogAiChat(state);
  const pageChat = router.selectors.pageAiChat(state);
  return dialogChat.isOpened ? dialogChat.isNewChat : (pageChat.isOpened ? pageChat.isNewChat : false);
};

/**
 * Returns whether this is a new chat session for a specific context.
 *
 * @param {Object} state - Redux state
 * @param {String} contextType - Context type: 'CARD' or 'BOARD'/'HOME'/'ONBOARDING'
 * @returns {Boolean} true if starting a new chat session
 */
export const isNewChatSessionByContext = (state, contextType) => {
  if (contextType === 'CARD') {
    return router.selectors.isDialogAiChatNewSession(state);
  }
  return router.selectors.isPageAiChatNewSession(state);
};

/**
 * Returns whether showing chat history view.
 * Reads from router state (showingHistory flag from URL param `aichat=show-history`).
 *
 * @param {Object} state - Redux state
 * @returns {Boolean} true if showing chat history
 */
export const showingChatHistory = (state) => {
  const dialogChat = router.selectors.dialogAiChat(state);
  const pageChat = router.selectors.pageAiChat(state);
  return dialogChat.isOpened ? dialogChat.showingHistory : (pageChat.isOpened ? pageChat.showingHistory : false);
};

/**
 * Returns whether showing chat history for a specific context.
 *
 * @param {Object} state - Redux state
 * @param {String} contextType - Context type: 'CARD' or 'BOARD'/'HOME'/'ONBOARDING'
 * @returns {Boolean} true if showing chat history
 */
export const showingChatHistoryByContext = (state, contextType) => {
  if (contextType === 'CARD') {
    return router.selectors.isDialogAiChatShowingHistory(state);
  }
  return router.selectors.isPageAiChatShowingHistory(state);
};

/**
 * Returns draft message text for an existing chat.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {String|null} Draft message text or null
 */
export const draftMessage = (state, chatId) => {
  if (!chatId) return null;
  const userId = auth.selectors.currentUserId(state);
  if (!userId) return null;
  // New format first (userId-scoped), fall back to old flat format for backward compat
  return get(state, `ai-chat.draftMessages.${userId}.${chatId}`, null)
    || get(state, `ai-chat.draftMessages.${chatId}`, null);
};

/**
 * Returns draft message text for a new chat in a specific context.
 *
 * The context key is generated from contextType, boardId, and cardId:
 * - Board context: `board_${boardId}`
 * - Card context: `card_${boardId}_${cardId}`
 * - Home context: `home`
 * - Onboarding context: `onboarding`
 *
 * @param {Object} state - Redux state
 * @param {String} contextType - Context type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * @param {String} boardId - Board ID (required for BOARD/CARD contexts)
 * @param {String} cardId - Card ID (for CARD context)
 * @returns {String|null} Draft message text or null
 */
export const newChatDraft = (state, contextType, boardId, cardId) => {
  const key = buildContextKey(contextType, boardId, cardId);
  if (!key) return null;
  const userId = auth.selectors.currentUserId(state);
  if (!userId) return null;

  return get(state, `ai-chat.newChatDrafts.${userId}.${key}`, null) || get(state, `ai-chat.newChatDrafts.${key}`, null);
};

/**
 * Returns the pre-generated chatId for a new chat with file attachments.
 * Set by ai-chat-input.attachFiles() when no chatId exists yet.
 * Persisted via persistEnhancer so the same chatId is used after page refresh.
 *
 * @param {Object} state - Redux state
 * @param {String} contextType - Context type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * @param {String} boardId - Board ID (required for BOARD/CARD contexts)
 * @param {String} cardId - Card ID (for CARD context)
 * @returns {String|null} Pre-generated chatId or null
 */
export const newChatId = (state, contextType, boardId, cardId) => {
  const key = buildContextKey(contextType, boardId, cardId);
  if (!key) return null;
  return get(state, `ai-chat.newChatIds.${key}`, null);
};

/**
 * Returns whether a message can be sent for the specified chat.
 *
 * A message can be sent when:
 * - No message is currently generating in this chat
 * - User has not exceeded concurrent request limit (5 messages across all chats)
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Optional chat ID. If not provided, uses currentChatId
 * @returns {Boolean} true if can send message
 */
export const canSendMessage = (state, chatId) => {
  const targetChatId = chatId || currentChatId(state);

  // For new chat sessions, check global concurrent limit only
  if (!targetChatId) {
    const concurrentCount = concurrentRequestCount(state);
    return concurrentCount < 5;
  }

  // For existing chats, check both per-chat and global limits
  const hasInProgress = hasInProgressMessage(state, targetChatId);
  if (hasInProgress) return false;

  const concurrentCount = concurrentRequestCount(state);
  return concurrentCount < 5;
};

/**
 * Returns the count of concurrent requests across all chats for current user.
 *
 * This counts all messages with status = 'GENERATING' across all chats.
 *
 * TODO: This selector needs to be refactored to use collection group query
 * According to Index 7 in wiki/redux/ai-chat-indexes.md, we need to:
 * 1. Set up a collection group query for 'ai-messages' filtered by status='GENERATING'
 * 2. Use firestoreRedux.selectors to read from that query
 *
 * For now, returning 0 to allow unlimited concurrent requests.
 *
 * @param {Object} state - Redux state
 * @returns {Number} Count of concurrent generating messages
 */
export const concurrentRequestCount = (state) => {
  // TODO: Implement collection group query for concurrent request counting
  // This requires:
  // - Setting up a Firestore listener for collection group 'ai-messages' where status = 'GENERATING'
  // - Reading from that query result in this selector
  //
  // Temporary implementation: Return 0 (no concurrent limit enforcement)
  return 0;
};

/**
 * Tool calls this client has already approved or rejected — see `resolvedToolCalls` in
 * the reducer.
 */
export const resolvedToolCalls = (state) => get(state, 'ai-chat.resolvedToolCalls', EMPTY_OBJECT);

/**
 * True while `tc` is still a live proposal: WAITING_FOR_APPROVAL on the server, and not
 * already resolved by this client. Approving localWrites the new status for instant
 * feedback, but a Firestore snapshot puts WAITING_FOR_APPROVAL back until the server's
 * own status arrives, which would flash the proposal onto the card again — so every
 * "is this proposal pending" test goes through here rather than reading `tc.status`.
 */
export const isAwaitingApproval = (tc, resolved) =>
  tc?.status === 'WAITING_FOR_APPROVAL' && !resolved?.[tc.id];

/**
 * The status a preview row should render for `tc`, with this client's own approve/reject
 * folded in. Same reason as `isAwaitingApproval`: approving localWrites IN_PROGRESS for
 * instant feedback, but a Firestore snapshot can put WAITING_FOR_APPROVAL back for a frame
 * before the server's status lands. Reading `tc.status` straight, the row would then flash
 * its checkbox back on (and empty, since the selection is already gone) mid-apply. Masking
 * only WAITING_FOR_APPROVAL keeps that transient off the screen; every real later status
 * (IN_PROGRESS / SUCCESS / FAILED …) passes through untouched.
 */
export const effectiveToolStatus = (tc, resolved) => {
  if (tc?.status !== 'WAITING_FOR_APPROVAL') return tc?.status;
  const outcome = resolved?.[tc?.id];
  if (outcome === 'APPROVED') return 'IN_PROGRESS';
  if (outcome === 'REJECTED') return 'REJECTED';
  return tc?.status;
};

/**
 * Returns all AIToolMessage documents for a specific chat.
 *
 * Reads from Firestore ai-tool-messages collection (loaded by loadToolMessagesSaga).
 * Query ID: `ai-tool-messages-${chatId}`
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {Array} Array of AIToolMessage documents
 */
export const allToolMessages = (state, chatId) => {
  if (!chatId) return [];

  return firestoreRedux.selectors.docsByQueryResult(
    state,
    `ai-tool-messages-${chatId}`,
    'ai-tool-messages'
  ) || [];
};

/**
 * Returns all tool calls for a specific response.
 *
 * Reads from Firestore ai-tool-messages collection, filters by responseId,
 * and flattens all toolCalls arrays into a single list ordered by invokedAt.
 *
 * AIToolMessage structure:
 * - id: aitm_$base62UUID
 * - chatId: Reference to parent chat
 * - responseId: Reference to specific response
 * - text: Explanatory text (may be null)
 * - toolCalls[]: Array of ToolCall objects
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} responseId - Response ID
 * @returns {Array} Array of ToolCall objects { name, status, input, output, error, invokedAt }
 */
const toolMessages = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, responseId) => responseId,
  (toolMessageDocs, responseId) => {
    if (!toolMessageDocs || toolMessageDocs.length === 0 || !responseId) {
      return [];
    }

    // Filter by responseId and collect all toolCalls
    const allToolCalls = [];
    for (const doc of toolMessageDocs) {
      if (doc.responseId === responseId && doc.toolCalls && doc.toolCalls.length > 0) {
        allToolCalls.push(...doc.toolCalls);
      }
    }

    // Sort by invokedAt timestamp
    return allToolCalls.sort((a, b) => {
      const aTime = a.invokedAt || 0;
      const bTime = b.invokedAt || 0;
      return aTime - bTime;
    });
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns default quick actions that appear in all contexts.
 * Combines results from two queries: contextTypes == null and contextTypes == [].
 *
 * @param {Object} state - Redux state
 * @returns {Array} Array of default quick actions
 */
export const defaultQuickActions = createSelector(
  (state) => firestoreRedux.selectors.docsByQueryResult(state, 'ai-quick-actions-default-null', 'ai-quick-actions'),
  (state) => firestoreRedux.selectors.docsByQueryResult(state, 'ai-quick-actions-default-empty', 'ai-quick-actions'),
  (nullQuickActions, emptyQuickActions) => [...(nullQuickActions || []), ...(emptyQuickActions || [])],
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  }
);

/**
 * Returns a quick action by name, searching both context-specific and default quick actions.
 *
 * The search order:
 * 1. Context-specific quick actions (ai-quick-actions-${contextType})
 * 2. Default quick actions (contextTypes == null or [])
 *
 * Context type is derived internally from `pageContext` selector.
 *
 * @param {Object} state - Redux state
 * @param {String} name - Quick action name (e.g., 'MAKE_BOARD_LIKE_THIS', 'CREATE_BOARD')
 * @returns {Object|null} Quick action object or null if not found
 */
export const quickActionByName = createSelector(
  (state) => {
    // Only the type is read, so not `pageContext`: its draft scan would run on every dispatch for each AI button.
    const context = basePageContext(state);
    return context ? firestoreRedux.selectors.docsByQueryResult(state, `ai-quick-actions-${context.type}`, 'ai-quick-actions') : [];
  },
  (state) => defaultQuickActions(state),
  (state, name) => name,
  (contextQuickActions, defaultActions, name) => {
    if (!name) return null;

    const contextActions = contextQuickActions || [];

    // Search context-specific first, then default
    const action = contextActions.find(qa => qa.name === name)
      || defaultActions.find(qa => qa.name === name)
      || null;

    if (action && action.disabled) {
      return null;
    }

    return action;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Returns a quick action by ID.
 *
 * @param {Object} state - Redux state
 * @param {String} quickActionId - Quick action ID (e.g., 'aiqa_xxx')
 * @returns {Object|null} Quick action object or null if not found
 */
export const quickAction = (state, quickActionId) => {
  if (!quickActionId) return null;
  return firestoreRedux.selectors.doc(state, 'ai-quick-actions', quickActionId) || null;
};

// ============================================================================
// Tool Preview Selectors
// ============================================================================

/**
 * How long the AI itself worked on a response, in seconds, or null while it is running, when
 * it did not end COMPLETED, or when nothing usable was recorded ("Worked on it").
 *
 * The message's first response uses the server's `meta.latency`, which also counts the
 * thinking before the first tool. Two cases fall back to the tool calls: the server writes 0
 * there for a turn that paused for approval, and times a regenerated response from the
 * original message's createdAt. The fallback starts at the first call's `invokedAt`; a turn
 * with an edit proposal ends at its last proposal's `invokedAt` (the proposal's later
 * timestamps move when the user approves, so they would count the user's wait), and any other
 * turn ends at the response's `statusAt`.
 *
 * Takes the response it is asked about rather than the latest: a regenerated message keeps
 * its earlier responses and the user can page back through them.
 *
 * @param {Object} state
 * @param {String} chatId
 * @param {String} messageId
 * @param {String} responseId
 * @returns {Number|null} seconds
 */
export const responseDuration = createSelector(
  (state, chatId, messageId) => message(state, chatId, messageId)?.responses,
  (state, chatId, messageId, responseId) => responseId,
  (state, chatId, messageId, responseId) => toolMessages(state, chatId, responseId),
  (state) => toolsConfig(state),
  (responses, responseId, responseToolCalls, conf) => {
    const response = (responses || []).find((entry) => entry.id === responseId);
    if (response?.status !== 'COMPLETED') return null;
    const toSeconds = (ms) => (ms > 0 ? Math.max(1, Math.round(ms / 1000)) : null);

    const firstResponse = [...responses].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))[0];
    const latency = response.meta?.latency;
    if (firstResponse?.id === responseId && latency > 0) return toSeconds(latency);

    const invokedTimes = responseToolCalls.map((tool) => tool.invokedAt).filter(Boolean);
    if (!invokedTimes.length) return null;
    const proposalNames = _proposalNames(conf);
    const proposalTimes = responseToolCalls
      .filter((tool) => proposalNames.has(tool.name))
      .map((tool) => tool.invokedAt)
      .filter(Boolean);
    const end = proposalTimes.length ? Math.max(...proposalTimes) : response.statusAt;
    return toSeconds(end - Math.min(...invokedTimes));
  },
  { memoize: lruMemoize, memoizeOptions: { maxSize: 50 } }
);

/**
 * The response's answer as it should read. The server can hand back `content` that starts
 * with the words it wrote ahead of each tool round, glued to the answer with nothing between
 * them (`...to confirm:Two cards...`); each of those stretches is put on its own paragraph.
 * Content that does not start that way comes back unchanged.
 *
 * @param {Object} state
 * @param {String} chatId
 * @param {String} messageId
 * @param {String} responseId
 * @returns {String} markdown
 */
export const responseContent = createSelector(
  (state, chatId, messageId, responseId) => (message(state, chatId, messageId)?.responses || [])
    .find((entry) => entry.id === responseId)?.content || '',
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, messageId, responseId) => responseId,
  (content, toolMessageDocs, responseId) => {
    const roundTexts = (toolMessageDocs || [])
      .filter((doc) => doc.responseId === responseId && doc.text)
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
      .map((doc) => doc.text);

    let answer = '';
    let rest = content;
    for (const text of roundTexts) {
      if (!rest.startsWith(text)) break;
      answer += text;
      rest = rest.slice(text.length);
      if (rest && !/\s$/.test(text) && !/^\s/.test(rest)) answer += '\n\n';
    }
    return answer + rest;
  },
  { memoize: lruMemoize, memoizeOptions: { maxSize: 50 } }
);

/**
 * Returns the AI chat tools configuration.
 *
 * Configuration structure:
 * {
 *   toolName: {
 *     component: 'component-name',  // Preview component name
 *     status: ['IN_PROGRESS', 'SUCCESS']  // Statuses at which to show preview
 *   }
 * }
 *
 * @param {Object} state - Redux state
 * @returns {Object} Tools configuration object
 */
export const toolsConfig = (state) => get(app.selectors.config(state), 'aiChat.tools', {});

/**
 * Returns the default tool preview component name.
 *
 * @param {Object} state - Redux state
 * @returns {String} Default preview component name
 */
export const defaultToolPreview = (state) => get(app.selectors.config(state), 'aiChat.defaultPreview', 'ai-chat-default-tool-preview');

/**
 * Returns tool calls for a specific message response.
 *
 * Behavior based on message status:
 * - GENERATING: Returns a default tool [{name: 'default'}] plus all IN_PROGRESS tools sorted by invokedAt
 * - Final state (COMPLETED, ERROR, CANCELLED): Returns every tool whose status is not PENDING
 *   (i.e. WAITING_FOR_APPROVAL / IN_PROGRESS / SUCCESS / FAILED / REJECTED / CANCELLED),
 *   sorted descending by invokedAt with completedAt as tiebreaker. PENDING is excluded
 *   because such tools are still being generated by the AI.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} messageId - Message ID
 * @param {String} responseId - Response ID
 * @returns {Array} Array of tool call objects
 */
export const toolCalls = createSelector(
  (state, chatId, messageId, responseId) => toolMessages(state, chatId, responseId),
  (state, chatId, messageId, responseId) => message(state, chatId, messageId),
  (state, chatId, messageId, responseId) => responseId,
  (allTools, msg, responseId) => {
    // Use response-level status when available; fall back to message status.
    // During regeneration the message status is GENERATING, but the previous
    // response(s) may already be in a final state (ERROR, COMPLETED, etc.).
    const currentResponse = msg?.responses?.find(r => r.id === responseId);
    const responseStatus = currentResponse?.status || msg?.status;

    // Check if response is in a known final state
    // Response status: GENERATING, COMPLETED, ERROR, CANCELLED
    // When response is not found (null) or status is undefined/GENERATING, treat as generating
    const isFinalState = ['COMPLETED', 'ERROR', 'CANCELLED'].includes(responseStatus);
    if (isFinalState) {
      if (!allTools || allTools.length === 0) {
        return [];
      }

      // Find the last tool by invokedAt, use completedAt as tiebreaker
      const sortedTools = [...allTools].sort((a, b) => {
        const aInvokedAt = a.invokedAt || 0;
        const bInvokedAt = b.invokedAt || 0;

        // Sort by invokedAt first
        if (aInvokedAt !== bInvokedAt) {
          return bInvokedAt - aInvokedAt; // Descending order
        }

        // If invokedAt is same, use completedAt as tiebreaker
        const aCompletedAt = a.completedAt || 0;
        const bCompletedAt = b.completedAt || 0;
        return bCompletedAt - aCompletedAt; // Descending order
      });

      // Return every non-PENDING tool. PENDING tools are still being generated by the AI
      // and not yet user-actionable; later config-based filtering in `visibleTools`
      // decides which of these statuses each tool ultimately surfaces.
      const finalTools = sortedTools.filter(tool => tool.status !== 'PENDING');
      return finalTools;
    }

    // Default: response is GENERATING or undefined - set default tool
    const isRegenerating = (msg?.responses?.length || 0) > 1 && currentResponse?.status === 'GENERATING';
    let tools = [{ name: 'default', isRegenerating }];

    // Check if allTools exists
    if (!allTools || allTools.length === 0) {
      return tools;
    }

    // Get all PENDING and IN_PROGRESS tools sorted by invokedAt
    const activeTools = allTools
      .filter(tool => tool.status === 'PENDING' || tool.status === 'IN_PROGRESS' || tool.status === 'WAITING_FOR_APPROVAL')
      .sort((a, b) => {
        const aTime = a.invokedAt || 0;
        const bTime = b.invokedAt || 0;
        return aTime - bTime;
      });

    // If active tools found, use them; otherwise keep default
    if (activeTools.length > 0) {
      tools = activeTools;
    }

    return tools;
  },
  {
    memoize: lruMemoize,
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

const _UNREGISTERED_TOOL_VISIBLE = new Set(['IN_PROGRESS', 'SUCCESS', 'FAILED']);

/**
 * Checks if tool is visible based on toolsConfig.
 * @param {Object} tool - Tool object
 * @param {Object} toolsConf - Tools configuration
 * @returns {Boolean} true if tool should be visible
 */
const _isToolVisibleByConfig = (tool, toolsConf) => {
  if (tool.name === 'default') {
    return true;
  }
  const toolConfig = toolsConf[tool.name];
  // Unregistered tool (not in aiChat.tools): show it through its whole life, not only while
  // it is working. A tool shipped before its wording is a gap in the mapping, and a row
  // carrying the raw tool name is what gets that gap noticed and reported — a row that
  // disappears the moment it finishes hides it. Only the statuses the default preview can
  // draw truthfully, though: it has no mark for a refused or cancelled step.
  if (!toolConfig) {
    return _UNREGISTERED_TOOL_VISIBLE.has(tool.status);
  }
  const configuredStatuses = toolConfig.status || [];
  return Array.isArray(configuredStatuses) && configuredStatuses.includes(tool.status);
};

/**
 * Rolls a set of tool calls into a single status, in the SAME raw status
 * vocabulary as an individual tool call — so a collection (an editCard group,
 * or a whole response) is interchangeable with a standalone tool call:
 *
 *   - WAITING_FOR_APPROVAL — any call still awaiting approval (incl. partially-applied)
 *   - IN_PROGRESS          — any call applying, none waiting
 *   - SUCCESS              — all terminal, >=1 succeeded
 *   - REJECTED             — all terminal, all rejected
 *   - CANCELLED            — all terminal, all cancelled
 *   - FAILED               — all terminal, >=1 failed, none succeeded
 *
 * @param {Array} tools - Tool calls to roll up
 * @returns {'WAITING_FOR_APPROVAL'|'IN_PROGRESS'|'SUCCESS'|'REJECTED'|'CANCELLED'|'FAILED'}
 */
const _aggregateStatus = (tools) => {
  if (!tools || tools.length === 0) return 'WAITING_FOR_APPROVAL';

  // Get an array of just the statuses for easy checking
  const statuses = tools.map(tool => tool.status);

  if (statuses.includes('WAITING_FOR_APPROVAL')) return 'WAITING_FOR_APPROVAL';
  if (statuses.includes('IN_PROGRESS')) return 'IN_PROGRESS';
  if (statuses.includes('SUCCESS')) return 'SUCCESS';

  if (statuses.every(status => status === 'REJECTED')) return 'REJECTED';
  if (statuses.every(status => status === 'CANCELLED')) return 'CANCELLED';

  if (statuses.includes('FAILED')) return 'FAILED';

  return 'WAITING_FOR_APPROVAL'; // Default fallback
};

/**
 * Final aggregate status of the response's card edits, in the raw status vocabulary
 * (see `_aggregateStatus`).
 *
 * Drives the same-card-scoped panel's final-status summary. Aggregates only the
 * card-targeting tool calls (`input.cardId`) — matching `isSameCardScopedPanel`, which
 * ignores info tools. Info tools (webSearch, load_skill, …) succeeding must not report the
 * edits as applied when the actual edit was rejected.
 *
 * Statuses are read through `effectiveToolStatus`: after Reject, a Firestore snapshot puts
 * WAITING_FOR_APPROVAL back until the server's REJECTED lands, which blanked the line
 * the user had just been shown.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} responseId - Response ID
 * @returns {'WAITING_FOR_APPROVAL'|'IN_PROGRESS'|'SUCCESS'|'REJECTED'|'CANCELLED'|'FAILED'}
 */
export const sameCardFinalStatus = createSelector(
  (state, chatId, responseId) => toolMessages(state, chatId, responseId),
  (state) => resolvedToolCalls(state),
  (responseToolCalls, resolved) => _aggregateStatus(responseToolCalls
    .filter((tc) => tc.input?.cardId)
    .map((tc) => ({ status: effectiveToolStatus(tc, resolved) }))),
  { memoizeOptions: { maxSize: 50 } }
);

/**
 * Whether the same-card panel is between the user's Apply and the server's outcome: some
 * card edit is IN_PROGRESS. Read through `effectiveToolStatus`, so a Firestore snapshot that
 * briefly puts WAITING_FOR_APPROVAL back does not blank the line mid-apply. Reads that carry
 * a `cardId` are left out, as they run IN_PROGRESS while the AI is still generating.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} responseId - Response ID
 * @returns {Boolean}
 */
export const isApplyingSameCardEdits = createSelector(
  (state, chatId, responseId) => toolMessages(state, chatId, responseId),
  (state) => resolvedToolCalls(state),
  (state) => toolsConfig(state),
  (responseToolCalls, resolved, conf) => {
    const proposalNames = _proposalNames(conf);
    return responseToolCalls.some((tc) => tc.input?.cardId && proposalNames.has(tc.name)
      && effectiveToolStatus(tc, resolved) === 'IN_PROGRESS');
  },
  { memoizeOptions: { maxSize: 50 } }
);

/** An AI edit proposal is a tool that needs approval (`WAITING_FOR_APPROVAL` in its config
 *  `status`) — createCard / deleteCard, and editCard via its leaf field-calls. */
const _isEditProposal = (name, conf) => (conf[name]?.status || []).includes('WAITING_FOR_APPROVAL');

/** Names of every approval-gated tool call: the proposal tools and their leaf tools. */
const _proposalNames = (conf) => {
  const names = new Set();
  for (const name in conf) {
    if (!_isEditProposal(name, conf)) continue;
    names.add(name);
    for (const leafName of conf[name].leafTools || []) names.add(leafName);
  }
  return names;
};

/** Edit proposals surface once actionable (`WAITING_FOR_APPROVAL`), while applying, or once
 *  terminal — never while still being generated (`PENDING`), so a proposal doesn't flash in
 *  the live preview mid-generation before the settled per-step layout takes over.
 *
 *  `IN_PROGRESS` belongs here: a proposal only reaches it after the user approves, so
 *  holding it back made the whole row vanish between Apply and the server's confirmation
 *  and then reappear as applied. Generation goes straight from `PENDING` to
 *  `WAITING_FOR_APPROVAL`, so nothing flashes by admitting it. */
const _EDIT_PROPOSAL_VISIBLE = new Set([
  'WAITING_FOR_APPROVAL', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'REJECTED', 'CANCELLED',
]);

/** Whether the AI is running for this message right now. Every other message status —
 *  `COMPLETED`, `ERROR`, `CANCELLED`, and `WAITING_FOR_USER_INPUT` (paused on the user's
 *  decision) — means it is not, so nothing can still be mid-call. */
const _isGenerating = (state, chatId, messageId) =>
  message(state, chatId, messageId)?.status === 'GENERATING';

/**
 * Filters + groups a raw list of ToolCalls by `toolsConfig`: drops calls whose current
 * status isn't in their tool's configured `status` array, and folds each parent tool's
 * `leafTools` (e.g. `editCard`'s field-level calls) into one grouped entry with an
 * aggregate status. Shared by `visibleTools` (whole-response, flattened) and
 * `editToolMessages` (per-`AiToolMessage`, so each execution step groups independently).
 *
 * Edit proposals are additionally held back until `WAITING_FOR_APPROVAL` / terminal (see
 * `_EDIT_PROPOSAL_VISIBLE`).
 *
 * @param {Array} tools - Raw ToolCall objects
 * @param {Object} conf - `toolsConfig` selector output
 * @param {Object} resolved - `resolvedToolCalls` selector output
 * @param {Boolean} generating - Whether the AI is running (see `_isGenerating`)
 * @returns {Array} Regular + grouped entries, sorted by `invokedAt` ascending
 */
const _computeVisibleTools = (tools, conf, resolved, generating) => {
  if (!tools?.length) return [];

  // 0. Fold this client's approve/reject into each call's status, so a status a snapshot
  //    briefly reverts to WAITING_FOR_APPROVAL keeps reading as applied/rejected — every
  //    downstream step (visibility, grouping, aggregate, the row itself) then sees the
  //    resolved status. See `effectiveToolStatus`.
  tools = tools.map((tool) => {
    const status = effectiveToolStatus(tool, resolved);
    return status === tool.status ? tool : { ...tool, status };
  });

  // 1. Create a parent-reference map for child tools
  const childToParentMap = {};
  for (const parentName in conf) {
    const pConf = conf[parentName];
    if (!pConf?.leafTools || !pConf?.groupBy) continue;
    for (const childName of pConf.leafTools) {
      childToParentMap[childName] = { name: parentName, groupBy: pConf.groupBy };
    }
  }

  const regularTools = [];
  const groupedToolsMap = {};

  // 2. Process all tools in a single pass
  for (const tool of tools) {
    const parentInfo = childToParentMap[tool.name];

    if (!parentInfo) {
      // --- Regular tools ---
      if (!_isToolVisibleByConfig(tool, conf)) continue;
      // Hold edit proposals (createCard / deleteCard) until approval / terminal.
      if (_isEditProposal(tool.name, conf)) {
        if (!_EDIT_PROPOSAL_VISIBLE.has(tool.status)) continue;
      } else if (!generating && (tool.status === 'PENDING' || tool.status === 'IN_PROGRESS')) {
        // The AI is not running, so a read still unfinished never reported back — its round
        // was abandoned by a network retry, the generation died mid-call, or it was stopped.
        // Left in, it would spin forever. Approved edits sit at IN_PROGRESS legitimately.
        continue;
      }
      regularTools.push(tool);
    } else {
      // --- Grouped (child) tools ---
      const groupKey = tool.input?.[parentInfo.groupBy];
      const parentConf = conf[parentInfo.name];

      // editCard leaves are edit proposals — same hold until approval / terminal.
      if (groupKey && parentConf?.status?.includes(tool.status) && _EDIT_PROPOSAL_VISIBLE.has(tool.status)) {
        const groupId = `${parentInfo.name}_${groupKey}`;

        if (!groupedToolsMap[groupId]) {
          groupedToolsMap[groupId] = {
            name: parentInfo.name,
            [parentInfo.groupBy]: groupKey,
            leafTools: [],
            invokedAt: tool.invokedAt || Infinity,
          };
        }

        groupedToolsMap[groupId].leafTools.push(tool);
        groupedToolsMap[groupId].invokedAt = Math.min(groupedToolsMap[groupId].invokedAt, tool.invokedAt || 0);
      }
    }
  }

  // 3. Map groups back to an array (with aggregate status) and combine everything
  const groupedTools = Object.values(groupedToolsMap).map((group) => {
    // Roll the leaf statuses into one aggregate. For an editCard group, split the
    // aggregated SUCCESS into Applied vs Partially applied (the 7-case table in
    // ai-helper.md) — the one distinction the shared 6-case _aggregateStatus collapses.
    let status = _aggregateStatus(group.leafTools);
    if (
      group.name === 'editCard' &&
      status === 'SUCCESS' &&
      group.leafTools.some((l) => l.status === 'REJECTED' || l.status === 'FAILED' || l.status === 'CANCELLED')
    ) {
      status = 'PARTIALLY_APPLIED';
    }
    return { ...group, status };
  });

  return [...regularTools, ...groupedTools].sort((a, b) => (a.invokedAt || 0) - (b.invokedAt || 0));
};

/**
 * Returns the currently visible tool calls for a specific message response: every
 * candidate tool whose current status is in its tool's configured `status` array,
 * `editCard`-style groups folded into one entry each (see `_computeVisibleTools`).
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} messageId - Message ID
 * @param {String} responseId - Response ID
 * @returns {Array} Array of visible tool call objects
 */
export const visibleTools = createSelector(
  (state, chatId, messageId, responseId) => toolCalls(state, chatId, messageId, responseId),
  (state) => toolsConfig(state),
  (state) => resolvedToolCalls(state),
  (state, chatId, messageId) => _isGenerating(state, chatId, messageId),
  (tools, conf, resolved, generating) => _computeVisibleTools(tools, conf, resolved, generating),
  { memoize: lruMemoize, memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 50 } }
);

/** The board a visibleTools entry targets — from `input.boardId` (mandatory on every tool
 *  call), or, for a synthesized editCard group (which carries no `input`), from its first
 *  leaf's `input.boardId`. */
const _entryBoardId = (entry) => entry?.input?.boardId || entry?.leafTools?.[0]?.input?.boardId || '';

/**
 * Names what a read step is working on, in words a person would recognise, or null.
 *
 * Null is a deliberate outcome, not a failure: `Reading the card crd_5Xyp…` is worse than
 * the wording alone, so anything that will not resolve is simply left off.
 *
 * Only the inputs that genuinely carry a name are read. `findCards` has no column field —
 * the model filters by `column.id` inside a query_string expression — and `search` buries
 * the user's words in `cardQuery`, so neither is mined for one.
 *
 * @param {Object} tool - a ToolCall
 * @param {Object} names - `_stepNames` output
 * @returns {String|null}
 */
const _stepTarget = (tool, names) => {
  const input = tool?.input || {};

  const oneBoard = (id) => (id ? names.boardNames[id] || null : null);

  switch (tool?.name) {
    case 'getBoard':
      return oneBoard(input.boardId);

    case 'getUnread':
    case 'findHighlightedCards':
      return oneBoard(input.boardId);

    case 'getCard': {
      const ids = input.cardIds || [];
      return ids.length === 1 ? names.cardTitles[ids[0]] || null : null;
    }

    // These take a board scope rather than a named subject. One board is worth saying;
    // several is not, and none means every board the user can reach.
    case 'findCards':
    case 'search':
    case 'getActivities':
    case 'getChats':
    case 'getAttachments': {
      const ids = input.boardIds || [];
      return ids.length === 1 ? oneBoard(ids[0]) : null;
    }

    case 'webSearch':
      return input.query || null;

    case 'webFetch':
      try {
        return input.url ? new URL(input.url).hostname.replace(/^www\./, '') : null;
      } catch {
        return null;
      }

    default:
      return null;
  }
};

// One `getCard` call can read several cards at once, so it counts each of them.
const _stepCount = (tool) => (tool.name === 'getCard' ? (tool.input?.cardIds || []).length || 1 : 1);

/**
 * Folds repeats of one tool inside a single round into one entry carrying a count, and
 * resolves each entry's target. Three identical lines say nothing three times; the count
 * also spares every locale a plural form.
 *
 * Edit entries are left alone — they are already grouped per card by `_computeVisibleTools`
 * and each one names its own card. So are the tools configured `keepVisible` (the new board
 * a turn created): each is a result of its own, not a repeat of the last.
 *
 * @param {Array} tools - entries from `_computeVisibleTools`, in invokedAt order
 * @param {Object} conf - `toolsConfig` output
 * @param {Object} names - `_stepNames` output
 * @returns {Array} entries, each with `isEdit`, `isPinned`, `count` and `target`
 */
const _foldRepeats = (tools, conf, names) => {
  const out = [];
  for (const tool of tools) {
    const isEdit = _isEditProposal(tool.name, conf) || Boolean(tool.leafTools);
    const isPinned = !isEdit && Boolean(conf[tool.name]?.keepVisible);
    const previous = out[out.length - 1];
    if (!isEdit && !isPinned && previous && previous.name === tool.name && !previous.leafTools) {
      previous.count += _stepCount(tool);
      // A folded row is only finished once every call in it is.
      if (previous.status === 'SUCCESS' && tool.status !== 'SUCCESS') previous.status = tool.status;
      // Calls that went to different subjects have no shared one, and naming the first
      // would pass off two boards as one. The wording then stands alone.
      if (previous.target !== _stepTarget(tool, names)) previous.target = null;
      continue;
    }
    out.push({
      ...tool,
      // Marked here so the view can fold the reads away without taking the proposals
      // with them: what the AI looked at is history, what it changed is not.
      isEdit,
      isPinned,
      count: _stepCount(tool),
      target: isEdit || isPinned ? null : _stepTarget(tool, names),
    });
  }
  return out;
};

/**
 * Groups a list of visibleTools-shaped entries by the board they target. Entries with no
 * resolvable board (info tools like webFetch) are dropped — only board-scoped proposals
 * (createCard / editCard / deleteCard) form groups. The chat's own `context.boardId` group
 * is ordered first; the rest follow sorted by board name.
 *
 * @param {Array} tools - visibleTools-shaped entries
 * @param {String} contextBoardId - The chat's `context.boardId`, ordered first when present
 * @param {Object} boardNames - board id to name, for the sort of the remaining groups
 * @returns {Array<{ boardId: String, isContextBoard: Boolean, entries: Array }>}
 */
const _groupByBoard = (tools, contextBoardId, boardNames) => {
  const byBoard = new Map();
  for (const entry of tools || []) {
    const boardId = _entryBoardId(entry);
    if (!boardId) continue;
    if (!byBoard.has(boardId)) byBoard.set(boardId, []);
    byBoard.get(boardId).push(entry);
  }

  const groups = [...byBoard.entries()].map(([boardId, entries]) => ({
    boardId,
    isContextBoard: boardId === contextBoardId,
    entries,
  }));

  // Context board first; the rest alphabetically by board name.
  return groups.sort((a, b) => {
    if (a.isContextBoard !== b.isContextBoard) return a.isContextBoard ? -1 : 1;
    return (boardNames[a.boardId] || '').localeCompare(boardNames[b.boardId] || '');
  });
};

/**
 * The names of just the boards and cards this response's tool calls point at, keyed by id.
 *
 * `editToolMessages` needs only these to word its steps and order its board groups. Taking
 * the whole `boards` and `cards` collections instead would re-run it, for every message on
 * screen, whenever any card anywhere changed. The equality check hands back the previous
 * object when none of these names moved, so `editToolMessages` sees the same input.
 */
const _stepNames = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, messageId, responseId) => responseId,
  (state) => firestoreRedux.selectors.collection(state, 'boards') || {},
  (state) => firestoreRedux.selectors.collection(state, 'cards') || {},
  (toolMessageDocs, responseId, boards, cards) => {
    const boardNames = {};
    const cardTitles = {};
    for (const doc of toolMessageDocs || []) {
      if (doc.responseId !== responseId) continue;
      for (const tool of doc.toolCalls || []) {
        const input = tool.input || {};
        for (const id of [input.boardId, ...(input.boardIds || [])]) {
          if (id) boardNames[id] = boards[id]?.name || '';
        }
        for (const id of input.cardIds || []) {
          if (id) cardTitles[id] = cards[id]?.title || '';
        }
      }
    }
    return { boardNames, cardTitles };
  },
  { memoize: lruMemoize, memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 50 } }
);

/**
 * Rounds whose every tool call is hidden leave nothing but the AI's words. Left alone they
 * stack as a run of identical `Thinking` lines with nothing between them, which reads as
 * repetition rather than as one continuous stretch of reasoning. A round that follows one
 * of those continues it, so their words are joined and the run becomes one disclosure
 * ahead of whatever the AI did next.
 */
const _joinSilentRounds = (rounds) => {
  const out = [];
  for (const round of rounds) {
    const previous = out[out.length - 1];
    if (previous && !previous.tools.length) {
      previous.text = [previous.text, round.text].filter(Boolean).join('\n\n');
      previous.tools = round.tools;
      previous.boardGroups = round.boardGroups;
      previous.stepCount += round.stepCount;
      continue;
    }
    out.push({ ...round });
  }
  return out;
};

/**
 * Returns this response's `AiToolMessage` docs, ordered by `createdAt` ascending, each
 * mapped to its own `text` + independently-grouped/filtered `tools` (+ `boardGroups` for
 * within-message cross-board headers) — unlike `toolMessages`/`toolCalls`/`visibleTools`,
 * which flatten every doc's `toolCalls` into one pooled list and discard the per-doc
 * `text`/`id`/`createdAt` grouping.
 *
 * Entries whose `text` is null and that count no step (e.g. a step whose only tool call
 * isn't visible by config) are dropped — nothing to render or count for that step.
 * `stepCount` also counts the reads still unfinished when the AI stopped, which `tools`
 * leaves out so they cannot spin forever.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} messageId - Message ID
 * @param {String} responseId - Response ID
 * @returns {Array<{ id: String, text: String, tools: Array, stepCount: Number, boardGroups: Array }>}
 */
export const editToolMessages = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, messageId, responseId) => responseId,
  (state) => toolsConfig(state),
  (state, chatId) => chat(state, chatId)?.context?.boardId || '',
  (state, chatId, messageId, responseId) => _stepNames(state, chatId, messageId, responseId),
  (state) => resolvedToolCalls(state),
  (state, chatId, messageId) => _isGenerating(state, chatId, messageId),
  (toolMessageDocs, responseId, conf, contextBoardId, names, resolved, generating) => {
    if (!toolMessageDocs?.length || !responseId) return [];

    const entries = toolMessageDocs
      .filter((doc) => doc.responseId === responseId)
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
      .map((doc) => {
        const tools = _foldRepeats(
          _computeVisibleTools(doc.toolCalls, conf, resolved, generating), conf, names);
        // Counted as though the AI were still running, so the reads it had started when it
        // stopped still count as steps even though they are no longer drawn.
        const stepCount = generating
          ? tools.length
          : _foldRepeats(_computeVisibleTools(doc.toolCalls, conf, resolved, true), conf, names).length;
        // Every round keeps its own `text` — what the AI wrote before it acted that round.
        // It used to be kept only for a round containing an edit tool, which threw away the
        // reasoning behind every read.
        return {
          id: doc.id,
          text: doc.text || '',
          tools,
          stepCount,
          boardGroups: _groupByBoard(tools, contextBoardId, names.boardNames),
        };
      })
      .filter((entry) => entry.text || entry.stepCount > 0);

    return _joinSilentRounds(entries);
  },
  { memoize: lruMemoize, memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 50 } }
);

/**
 * Returns the ids of every ToolCall in the given response that is currently in
 * `WAITING_FOR_APPROVAL` status. Used by `ai-chat-message-response` to wire the
 * batch "Apply all" / "Reject all" buttons, and to filter stale selections.
 *
 * Built on `toolMessages`, which already flattens and response-scopes the calls,
 * so this is the single source of truth for "what's still awaiting approval".
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} responseId - Response ID
 * @returns {String[]}
 */
export const waitingToolCallIds = createSelector(
  (state, chatId, responseId) => toolMessages(state, chatId, responseId),
  (state) => resolvedToolCalls(state),
  (toolCalls, resolved) => toolCalls
    .filter(tc => isAwaitingApproval(tc, resolved))
    .map(tc => tc.id),
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 50 } }
);

/**
 * Returns true if the response has any tool call awaiting user approval — i.e. any
 * WAITING_FOR_APPROVAL tool call in it. The chat-level `pendingEdits` boolean was removed
 * (replaced by `pendingEditBoardIds`), so the WAITING tool calls are the signal now.
 *
 * Used by ai-chat-message-response to decide whether to render the batch
 * Apply all / Reject all buttons below the AI message text.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} responseId - Response ID
 * @returns {Boolean}
 */
export const pendingEdits = createSelector(
  (state, chatId, responseId) => waitingToolCallIds(state, chatId, responseId),
  (waitingIds) => waitingIds.length > 0
);

/**
 * Returns true when the AI Helper panel should render the same-card carve-out
 * UI — single [Apply] / [Reject] pair while pending, replaced by a final-status
 * summary once all leaves are terminal. The per-tool rows are suppressed in
 * both states.
 *
 * Sticky across the response lifecycle: stays true through WAITING → IN_PROGRESS
 * → terminal so the final-status message can replace the buttons in place.
 *
 * Conditions, all required:
 *   - The chat doc has a non-null `context.cardId` (chat is opened on a card).
 *   - At least one ToolCall targets that card (`input.cardId` === it).
 *   - No ToolCall targets a different card. Info tools (webSearch, etc.) carry
 *     no `input.cardId` and are ignored.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} responseId - Response ID
 * @returns {Boolean}
 */
export const isSameCardScopedPanel = createSelector(
  (state, chatId) => chat(state, chatId),
  (state, chatId, responseId) => toolMessages(state, chatId, responseId),
  (chatDoc, responseToolCalls) => {
    const scopedCardId = chatDoc?.context?.cardId;
    if (!scopedCardId || responseToolCalls.length === 0) return false;
    // Skip info tools (no cardId). Need one call on this card, none on another.
    let targetsScopedCard = false;
    for (const tc of responseToolCalls) {
      const cardId = tc.input?.cardId;
      if (cardId == null) continue;
      if (cardId !== scopedCardId) return false;
      targetsScopedCard = true;
    }
    return targetsScopedCard;
  },
  { memoizeOptions: { maxSize: 50 } }
);

/**
 * Returns the cardId currently being hovered in any AI Helper tool-call row,
 * or `null`. Drives the `[ai-hovered]` host attribute on <kerika-board-card>.
 *
 *  @param {Object} state - Redux state
 * @returns {String|null}
 */
export const hoverCardId = (state) => state?.['ai-chat']?.hoverCardId || null;

/**
 * UI-field → ToolCall name mapping for the per-field diff components in the
 * card-detail dialog.
 */
const FIELD_TO_TOOL_NAME = {
  title: 'updateCardTitle',
  description: 'updateCardDescription',
  dueDate: 'updateCardDueDate',
  priority: 'updateCardPriority',
  status: 'updateCardStatus',
  tags: 'updateCardTags',
  assignees: 'updateCardAssignments',
  columnId: 'moveCard',
};

/**
 * Returns the single WAITING ToolCall (with its parent `responseId` attached)
 * that targets the given card + field, or `null`. Read by the per-field diff
 * components (e.g. <card-title-diff>) to drive their Approve / Reject
 * dispatches without each component having to walk all tool messages.
 *
 * If multiple WAITING ToolCalls match the same (cardId, field) — which
 * shouldn't happen normally — the first one encountered wins.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent chat id
 * @param {String} cardId - Target card id
 * @param {String} fieldName - UI field name, e.g. 'title', 'description'.
 *   See FIELD_TO_TOOL_NAME for the supported set.
 * @returns {Object|null} `{ id, responseId, name, input, status, ... }` or null.
 */
export const toolCallForField = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, cardId) => cardId,
  (state, chatId, cardId, fieldName) => fieldName,
  (state) => resolvedToolCalls(state),
  (toolMessageDocs, cardId, fieldName, resolved) => {
    const toolName = FIELD_TO_TOOL_NAME[fieldName];
    if (!toolName || !cardId || !toolMessageDocs) return null;
    for (const doc of toolMessageDocs) {
      if (!Array.isArray(doc.toolCalls)) continue;
      for (const tc of doc.toolCalls) {
        if (!isAwaitingApproval(tc, resolved)) continue;
        if (tc.name !== toolName) continue;
        if (tc.input?.cardId !== cardId) continue;
        // Attach the parent message's responseId so the caller can dispatch
        // approveToolCalls / rejectToolCalls without re-walking the docs.
        return { ...tc, responseId: doc.responseId };
      }
    }
    return null;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

// Board-setting UI field → the tool that changes it. Keyed by the board doc field the saga
// overlays (`viewSettings` is the per-user dashboard toggle). The board analog of
// FIELD_TO_TOOL_NAME — separate because `name` / `description` mean different tools per entity.
const BOARD_SETTING_FIELD_TO_TOOL = {
  name: 'updateBoardName',
  description: 'updateBoardDescription',
  privacy: 'updateBoardPrivacy',
  wipLimitEnabled: 'updateBoardWipLimitEnabled',
  showCardRefNo: 'updateBoardAutoNumbering',
  tagsEnabled: 'updateBoardTagsEnabled',
  viewSettings: 'updateBoardInViewSettings',
};

/**
 * Board-settings counterpart of `toolCallForField`: the single WAITING ToolCall (with its parent
 * `responseId`) that targets the given board + setting field, or `null`. Matches on `input.boardId`
 * and the per-field board tool. Read by the board-setting diff components to drive Apply / Reject.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent AI chat id
 * @param {String} boardId - Target board id
 * @param {String} field - Board doc field, e.g. 'name', 'privacy'. See BOARD_SETTING_FIELD_TO_TOOL.
 * @returns {Object|null}
 */
export const boardSettingToolCallForField = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, boardId) => boardId,
  (state, chatId, boardId, field) => field,
  (state) => resolvedToolCalls(state),
  (toolMessageDocs, boardId, field, resolved) => {
    const toolName = BOARD_SETTING_FIELD_TO_TOOL[field];
    if (!toolName || !boardId || !toolMessageDocs) return null;
    for (const doc of toolMessageDocs) {
      if (!Array.isArray(doc.toolCalls)) continue;
      for (const tc of doc.toolCalls) {
        if (!isAwaitingApproval(tc, resolved)) continue;
        if (tc.name !== toolName) continue;
        if (tc.input?.boardId !== boardId) continue;
        return { ...tc, responseId: doc.responseId };
      }
    }
    return null;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * Tool-name → sub-entity tab type matchers. A pending sub-entity ToolCall belongs
 * to a tab by what its name targets — covering every create / update / delete /
 * move variant without enumerating them.
 */
const SUB_ENTITY_MATCHERS = {
  task: (name) => name.includes('Subtask'),
  attachment: (name) => name.includes('Attachment'),
  chat: (name) => name.includes('ChatMessage'),
};

/**
 * Returns the WAITING_FOR_APPROVAL sub-entity ToolCalls targeting a card for a
 * given sub-entity tab type, each with its parent message's `responseId`. Drives
 * the per-tab AI pending-changes indicator (TASKS / ATTACHMENTS / CHATS) in the
 * card-detail dialog — the border clears automatically once the matching calls
 * leave WAITING_FOR_APPROVAL (this selector then returns []).
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent chat id
 * @param {String} cardId - Target card id
 * @param {String} subEntityType - 'task' | 'attachment' | 'chat'
 * @returns {Array} matching ToolCalls, empty when none are pending
 */
export const pendingSubEntityToolCalls = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, cardId) => cardId,
  (state, chatId, cardId, subEntityType) => subEntityType,
  (state) => firestoreRedux.selectors.collection(state, 'tasks'),
  (state) => resolvedToolCalls(state),
  (toolMessageDocs, cardId, subEntityType, taskDocs, resolved) => {
    const matches = SUB_ENTITY_MATCHERS[subEntityType];
    if (!matches || !cardId || !toolMessageDocs) return [];
    const result = [];
    for (const doc of toolMessageDocs) {
      if (!Array.isArray(doc.toolCalls)) continue;
      for (const tc of doc.toolCalls) {
        if (!isAwaitingApproval(tc, resolved)) continue;
        if (tc.input?.cardId !== cardId) continue;
        if (typeof tc.name !== 'string' || !matches(tc.name)) continue;
        // Attach the saga-enriched task doc (undefined until the overlay lands or
        // for sub-entities without a subtaskId); the original toolCall is spread,
        // not mutated.
        const subtaskId = tc.input?.subtaskId;
        result.push({ ...tc, responseId: doc.responseId, task: subtaskId ? taskDocs?.[subtaskId] : undefined });
      }
    }
    return result;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * Collects one board-edit surface's `WAITING_FOR_APPROVAL` leaves on `boardId` — matched
 * against the surface's own `leafTools` (the explicit list its `aiChat.tools` entry declares,
 * NEVER a substring: `updateBoardTagsEnabled` is a settings tool that contains `BoardTag`).
 * Each leaf carries `id`, `name`, `input` and `responseId` for the row's checkbox and the
 * batch approve / reject. Shared by the six `pendingBoard*ToolCalls` selectors.
 */
const _pendingBoardSurfaceToolCalls = (toolMessageDocs, resolved, leafTools, boardId) => {
  if (!boardId || !leafTools?.length) return [];
  const names = new Set(leafTools);
  const result = [];
  for (const doc of toolMessageDocs || []) {
    if (!Array.isArray(doc.toolCalls)) continue;
    for (const tc of doc.toolCalls) {
      if (!isAwaitingApproval(tc, resolved)) continue;
      if (tc.input?.boardId !== boardId) continue;
      if (typeof tc.name !== 'string' || !names.has(tc.name)) continue;
      result.push({ ...tc, responseId: doc.responseId });
    }
  }
  return result;
};

/**
 * One board-edit section's `WAITING_FOR_APPROVAL` leaves on `boardId`, keyed by section
 * (`boardTags`, `boardChats`, …). Reads the section's `leafTools` from the tool config, so the
 * tool list has a single source of truth. The row component uses this directly with the
 * synthesised entry's `name`; the six named selectors below wrap it for the diff components,
 * which each know their own section.
 */
export const pendingBoardSectionToolCalls = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state) => resolvedToolCalls(state),
  (state, chatId, boardId, section) => toolsConfig(state)[section]?.leafTools,
  (state, chatId, boardId) => boardId,
  (toolMessageDocs, resolved, leafTools, boardId) =>
    _pendingBoardSurfaceToolCalls(toolMessageDocs, resolved, leafTools, boardId),
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

export const pendingBoardChatToolCalls = (state, chatId, boardId) => pendingBoardSectionToolCalls(state, chatId, boardId, 'boardChats');
export const pendingBoardAttachmentToolCalls = (state, chatId, boardId) => pendingBoardSectionToolCalls(state, chatId, boardId, 'boardAttachments');
export const pendingBoardTeamToolCalls = (state, chatId, boardId) => pendingBoardSectionToolCalls(state, chatId, boardId, 'boardTeam');
export const pendingBoardColumnToolCalls = (state, chatId, boardId) => pendingBoardSectionToolCalls(state, chatId, boardId, 'boardColumns');
export const pendingBoardTagToolCalls = (state, chatId, boardId) => pendingBoardSectionToolCalls(state, chatId, boardId, 'boardTags');
export const pendingBoardSettingToolCalls = (state, chatId, boardId) => pendingBoardSectionToolCalls(state, chatId, boardId, 'boardSettings');

/**
 * The board's pending tag proposals, keyed by the tag each one changes — `{ [tagId]: ToolCall[] }`.
 * A create / update / delete is keyed by its own `boardTagId`; a merge is keyed under every source
 * tag it removes. The keys are the tags that render as a `board-tag-diff`, and each value is that
 * tag's leaves for Apply / Reject (a rename + colour change on one tag arrive together).
 */
export const boardTagProposals = createSelector(
  (state, chatId, boardId) => pendingBoardTagToolCalls(state, chatId, boardId),
  (toolCalls) => {
    const proposalsByTag = {};
    for (const toolCall of toolCalls || []) {
      const tagIds = [];
      if (toolCall.input?.boardTagId) tagIds.push(toolCall.input.boardTagId);
      // A merge keys under each source tag it removes (guard the array — a stray string would
      // otherwise spread into single characters, mirroring the saga's own Array.isArray check).
      if (toolCall.name === 'mergeBoardTags' && Array.isArray(toolCall.input?.sourceTagIds)) {
        tagIds.push(...toolCall.input.sourceTagIds);
      }
      for (const tagId of tagIds) (proposalsByTag[tagId] = proposalsByTag[tagId] || []).push(toolCall);
    }
    return proposalsByTag;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 50 } }
);

/**
 * The board's pending column proposals, keyed by the column each one changes —
 * `{ [columnId]: ToolCall[] }`. A create / update / delete is keyed by its own `columnId`; a
 * rename, a move, a WIP-limit change and a visibility change on one column arrive on one call and
 * decide together.
 */
export const boardColumnProposals = createSelector(
  (state, chatId, boardId) => pendingBoardColumnToolCalls(state, chatId, boardId),
  (toolCalls) => {
    const proposalsByColumn = {};
    for (const toolCall of toolCalls || []) {
      const columnId = toolCall.input?.columnId;
      if (columnId) (proposalsByColumn[columnId] = proposalsByColumn[columnId] || []).push(toolCall);
    }
    return proposalsByColumn;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 50 } }
);

/**
 * The board's pending team proposals, keyed by the member (user id) each one changes —
 * `{ [userId]: ToolCall[] }`. An add / role-change / remove is keyed by its own `userId`; the host
 * renders that member as a `board-team-member-diff`.
 */
export const boardTeamProposals = createSelector(
  (state, chatId, boardId) => pendingBoardTeamToolCalls(state, chatId, boardId),
  (state) => firestoreRedux.selectors.collection(state, 'users'),
  (toolCalls, users) => {
    const proposalsByMember = {};
    for (const toolCall of toolCalls || []) {
      // An addBoardTeamMember tool keys the member by `userId`, or by `email` (an existing user
      // resolved to their id) — so a proposal by email still lands on the right member row.
      const userId = toolCall.input?.userId || boardUtils.userIdByEmail(users, toolCall.input?.email);
      if (userId) (proposalsByMember[userId] = proposalsByMember[userId] || []).push(toolCall);
    }
    return proposalsByMember;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 50 } }
);

/** The six board-edit surface keys, in `aiChat.tools`. */
const _boardSurfaceKeys = ['boardChats', 'boardAttachments', 'boardTeam', 'boardColumns', 'boardTags', 'boardSettings'];

/**
 * The board-edit sections that have `WAITING_FOR_APPROVAL` proposals on `boardId`, as a Set of
 * section keys. Board-scoped rather than chat-scoped — the board toolbar has a board, not a
 * chat — so it reads every chat feeding this board through the live `ai-chats-incoming-edits`
 * query the board saga already maintains, and flattens their tool messages. Drives the
 * toolbar's per-section proposal outline. The `isEqual` result guard keeps the Set referentially
 * stable across dispatches that don't change it.
 */
export const boardPendingProposalSections = createSelector(
  (state, boardId) => {
    const chats = boardId
      ? firestoreRedux.selectors.docsByQueryResult(state, `ai-chats-incoming-edits-${boardId}`, 'ai-chats') || []
      : [];
    const docs = [];
    for (const chat of chats) {
      const messages = firestoreRedux.selectors.docsByQueryResult(state, `ai-tool-messages-${chat.id}`, 'ai-tool-messages') || [];
      for (const message of messages) docs.push(message);
    }
    return docs;
  },
  (state) => resolvedToolCalls(state),
  (state) => toolsConfig(state),
  (state, boardId) => boardId,
  (toolMessageDocs, resolved, config, boardId) => {
    const surfaces = new Set();
    if (!boardId) return surfaces;
    for (const surface of _boardSurfaceKeys) {
      const leafTools = config[surface]?.leafTools;
      if (_pendingBoardSurfaceToolCalls(toolMessageDocs, resolved, leafTools, boardId).length) {
        surfaces.add(surface);
      }
    }
    return surfaces;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 50 } }
);

/**
 * Pending sub-task diffs for a card, grouped by subtaskId. A single sub-task can
 * carry several proposed changes (e.g. updateSubtaskTitle + updateSubtaskAssignments)
 * — this folds them into ONE entry so the UI renders a single diff row whose
 * Apply/Reject act on all its toolCalls together. Each group carries the sub-task's
 * saga-enriched `task` doc (same for every toolCall of that sub-task).
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent chat id
 * @param {String} cardId - Target card id
 * @returns {Array<{ subtaskId: String, task: Object, toolCalls: Array }>}
 */
export const pendingSubtaskDiffs = createSelector(
  (state, chatId, cardId) => pendingSubEntityToolCalls(state, chatId, cardId, 'task'),
  (toolCalls) => {
    const groups = new Map();
    for (const tc of toolCalls || []) {
      const subtaskId = tc.input?.subtaskId;
      if (!subtaskId) continue;
      const group = groups.get(subtaskId) || { subtaskId, task: tc.task, toolCalls: [] };
      group.toolCalls.push(tc);
      groups.set(subtaskId, group);
    }
    return [...groups.values()];
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * Pending attachment diffs for a card, grouped by attachmentId. Mirrors
 * pendingSubtaskDiffs: folds a proposal's ToolCalls into one entry and attaches the
 * saga-enriched `card-attachments` doc (type/subType/url/title + aiPending flags) as
 * `attachment`, so the ATTACHMENTS tab renders one diff row per attachment.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent chat id
 * @param {String} cardId - Target card id
 * @returns {Array<{ attachmentId: String, attachment: Object, toolCalls: Array }>}
 */
export const pendingAttachmentDiffs = createSelector(
  (state, chatId, cardId) => pendingSubEntityToolCalls(state, chatId, cardId, 'attachment'),
  (state) => firestoreRedux.selectors.collection(state, 'card-attachments'),
  (toolCalls, attachmentDocs) => {
    const groups = new Map();
    for (const tc of toolCalls || []) {
      const attachmentId = tc.input?.attachmentId;
      if (!attachmentId) continue;
      const group = groups.get(attachmentId) || {
        attachmentId,
        attachment: attachmentDocs?.[attachmentId],
        toolCalls: [],
      };
      group.toolCalls.push(tc);
      groups.set(attachmentId, group);
    }
    return [...groups.values()];
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * Board-attachment counterpart of `pendingAttachmentDiffs`. Groups the board's pending attachment
 * leaves by attachment and pairs each with its `board-attachments` doc (the saga's localWrite
 * overlay), so the board attachments view renders one diff row per proposed attachment.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent AI chat id
 * @param {String} boardId - Target board id
 * @returns {Array<{ attachmentId: String, attachment: Object, toolCalls: Array }>}
 */
export const pendingBoardAttachmentDiffs = createSelector(
  (state, chatId, boardId) => pendingBoardAttachmentToolCalls(state, chatId, boardId),
  (state) => firestoreRedux.selectors.collection(state, 'board-attachments'),
  (toolCalls, attachmentDocs) => {
    const groups = new Map();
    for (const tc of toolCalls || []) {
      const attachmentId = tc.input?.attachmentId;
      if (!attachmentId) continue;
      const group = groups.get(attachmentId) || {
        attachmentId,
        attachment: attachmentDocs?.[attachmentId],
        toolCalls: [],
      };
      group.toolCalls.push(tc);
      groups.set(attachmentId, group);
    }
    return [...groups.values()];
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * Pending chat diffs for a card, grouped by chatMessageId. Mirrors pendingAttachmentDiffs:
 * folds a proposal's ToolCalls into one entry and attaches the materialized
 * `card-chat-messages` doc (the saga's localWrite overlay) as `chatMessage`, so the CHATS
 * tab renders one diff row per proposed message.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent chat id
 * @param {String} cardId - Target card id
 * @returns {Array<{ chatMessageId: String, chatMessage: Object, toolCalls: Array }>}
 */
export const pendingChatDiffs = createSelector(
  (state, chatId, cardId) => pendingSubEntityToolCalls(state, chatId, cardId, 'chat'),
  (state) => firestoreRedux.selectors.collection(state, 'card-chat-messages'),
  (toolCalls, chatMessageDocs) => {
    const groups = new Map();
    for (const tc of toolCalls || []) {
      const chatMessageId = tc.input?.chatMessageId;
      if (!chatMessageId) continue;
      const group = groups.get(chatMessageId) || {
        chatMessageId,
        chatMessage: chatMessageDocs?.[chatMessageId],
        toolCalls: [],
      };
      group.toolCalls.push(tc);
      groups.set(chatMessageId, group);
    }
    return [...groups.values()];
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * Board-chat counterpart of `pendingChatDiffs`. Groups the board's pending chat leaves by message
 * and pairs each with its `board-chat-messages` doc (the saga's localWrite overlay), so the board
 * chat renders one diff row per proposed message.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent AI chat id
 * @param {String} boardId - Target board id
 * @returns {Array<{ chatMessageId: String, chatMessage: Object, toolCalls: Array }>}
 */
export const pendingBoardChatDiffs = createSelector(
  (state, chatId, boardId) => pendingBoardChatToolCalls(state, chatId, boardId),
  (state) => firestoreRedux.selectors.collection(state, 'board-chat-messages'),
  (toolCalls, chatMessageDocs) => {
    const groups = new Map();
    for (const tc of toolCalls || []) {
      const chatMessageId = tc.input?.chatMessageId;
      if (!chatMessageId) continue;
      const group = groups.get(chatMessageId) || {
        chatMessageId,
        chatMessage: chatMessageDocs?.[chatMessageId],
        toolCalls: [],
      };
      group.toolCalls.push(tc);
      groups.set(chatMessageId, group);
    }
    return [...groups.values()];
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * The chat whose pending AI edits target this card on this board, or null. Scans every
 * incoming-edit chat's WAITING tool calls, so proposals from more than one chat all
 * surface — each card resolving to its own owning chat. (The server should keep it to one
 * chat per board; when it doesn't, we show them all instead of hiding the older ones.) On
 * the rare card claimed by two chats, the newer chat wins. `userId` is an optional guard
 * (the query is already user-scoped).
 *
 * @param {Object} state - Redux state
 * @param {String} boardId - Board ID
 * @param {String} cardId - Card ID
 * @param {String} [userId] - Current user id (optional guard)
 * @returns {String|null}
 */
export const pendingEditChatId = (state, boardId, cardId, userId) =>
  (cardId && _pendingEditChatIdsByCard(state, boardId, userId).get(cardId)) || null;

// One pass over the tool messages per board, instead of one per card on the board.
const _pendingEditChatIdsByCard = createSelector(
  (state, boardId) => firestoreRedux.selectors.docsByQueryResult(state, `ai-chats-incoming-edits-${boardId}`, 'ai-chats'),
  (state) => firestoreRedux.selectors.collection(state, 'ai-tool-messages'),
  (state, boardId, userId) => userId,
  (state) => resolvedToolCalls(state),
  (chats, toolMessagesColl, userId, resolved) => {
    const ownerIds = new Map();
    const chatSentAt = new Map(
      (chats || [])
        .filter((c) => c?.id && (!userId || c.userId === userId))
        .map((c) => [c.id, c.latestMessageSentAt || 0]),
    );
    if (!chatSentAt.size) return ownerIds;
    for (const tm of Object.values(toolMessagesColl || {})) {
      if (!chatSentAt.has(tm.chatId)) continue;
      const cardIds = new Set(
        (tm.toolCalls || []).filter((tc) => isAwaitingApproval(tc, resolved) && tc.input?.cardId).map((tc) => tc.input.cardId),
      );
      for (const cardId of cardIds) {
        const ownerId = ownerIds.get(cardId);
        if (ownerId && chatSentAt.get(tm.chatId) < chatSentAt.get(ownerId)) continue;
        ownerIds.set(cardId, tm.chatId);
      }
    }
    return ownerIds;
  },
  { memoizeOptions: { maxSize: 50 } },
);

/**
 * The most recent chat with pending AI edits touching this board — its OWN chat or another
 * board's — for the given user, or null. Reads the `ai-chats-incoming-edits-${boardId}` query,
 * whose `pendingEditBoardIds array-contains boardId` match covers both: the target board of a
 * cross-board edit AND the source board of an edit made from its own chat (so a card moved off
 * a board, or edited from the board's / a card's own chat, still leaves a trace on it).
 *
 * When non-null, ai-chat-fab-button replaces the AI Helper FAB with the [View AI Chat] /
 * [Apply all] / [Reject all] bar.
 *
 * @param {Object} state - Redux state
 * @param {String} boardId - Board id
 * @param {String} [userId] - Current user id (optional guard; the query is already user-scoped)
 * @returns {Object|null}
 */
export const pendingEditsChatForBoard = (state, boardId, userId) => {
  const chats = firestoreRedux.selectors.docsByQueryResult(state, `ai-chats-incoming-edits-${boardId}`, 'ai-chats') || [];
  const matching = chats.filter((c) => c && (!userId || c.userId === userId) && c.context?.boardId);
  // Also a chat that ORIGINATED on this board with a pending cross-board move OUT of it — its
  // edits land on another board, so the incoming query misses it, but its source board should
  // still surface the pending action (Apply / Reject / View). Match by context board.
  const allChats = get(state, 'firestore.docs.ai-chats') || {};
  for (const chatId in allChats) {
    const chat = allChats[chatId];
    if (chat && chat.context?.boardId === boardId && (!userId || chat.userId === userId)
      && Array.isArray(chat.pendingEditBoardIds) && chat.pendingEditBoardIds.length
      && !matching.some((existing) => existing.id === chatId)) {
      matching.push(chat);
    }
  }
  if (!matching.length) return null;
  return matching.reduce(
    (latest, c) => ((c.latestMessageSentAt || 0) > (latest.latestMessageSentAt || 0) ? c : latest),
    matching[0],
  );
};

/**
 * A chat's WAITING tool calls relevant to `boardId` — all from a single response (the latest)
 * and each stamped with its `responseId`. Lets that board's [Apply all] / [Reject all]
 * approve/reject exactly its edits. A board's edits are those landing on it, plus (when it's
 * the chat's own board) a cross-board move leaving it — so both the source and the destination
 * board of a move can act on it. Returning one response's calls keeps them consistent for
 * `dispatchApproval` / `dispatchRejection`, which act on one responseId. Pairs with
 * `pendingEditsChatForBoard`, which supplies the `chatId`.
 *
 * @param {Object} state
 * @param {String} boardId - The board the edits are relevant to (destination, or source of a move)
 * @param {String} chatId - The chat holding the edits (the board's own or another's)
 * @returns {Array} WAITING ToolCalls for this board from one response, each with `responseId`
 */
export const pendingBoardToolCalls = createSelector(
  (state, boardId, chatId) => allToolMessages(state, chatId),
  (state, boardId) => boardId,
  (state, boardId, chatId) => get(state, `firestore.docs.ai-chats.${chatId}.context.boardId`),
  (state) => resolvedToolCalls(state),
  (toolMessages, boardId, chatContextBoardId, resolved) => {
    // Approve / reject only work on one response at a time — mixing responses makes the
    // backend drop all but one. So keep just the latest response's waiting calls.
    const isSourceBoard = chatContextBoardId === boardId;
    const matches = [];
    for (const tm of toolMessages || []) {
      for (const tc of tm.toolCalls || []) {
        if (!isAwaitingApproval(tc, resolved)) continue;
        const landsOnThisBoard = tc.input?.boardId === boardId;
        // On the chat's own (source) board, also include a cross-board move leaving it — its
        // edit lands on the destination board, so `input.boardId` is that other board.
        const leavesThisBoard = isSourceBoard && tc.name === 'moveCard'
          && tc.input?.boardId && tc.input.boardId !== boardId;
        if (landsOnThisBoard || leavesThisBoard) {
          matches.push({ ...tc, responseId: tm.responseId });
        }
      }
    }
    if (!matches.length) return [];
    const latest = matches.reduce((a, b) => ((b.invokedAt || 0) > (a.invokedAt || 0) ? b : a));
    return matches.filter((tc) => tc.responseId === latest.responseId);
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 50 } },
);

/**
 * Returns createCard ToolCalls in WAITING_FOR_APPROVAL for a column.
 *
 * Used by cardsByColumn (board/selectors.js) to inject pending new-card previews.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} columnId - Column ID
 * @returns {Array} Array of createCard ToolCall objects
 */
export const pendingNewCards = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, columnId) => columnId,
  (state) => resolvedToolCalls(state),
  (toolMessageDocs, columnId, resolved) => {
    if (!toolMessageDocs || !columnId) return [];
    const result = [];
    for (const doc of toolMessageDocs) {
      if (!doc.toolCalls) continue;
      for (const tc of doc.toolCalls) {
        if (tc.name === 'createCard' && isAwaitingApproval(tc, resolved) && tc.input?.columnId === columnId) {
          result.push(tc);
        }
      }
    }
    return result;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 50 } }
);

/**
 * A board's chat tools are named `createBoardChatMessage` / `updateBoardChatMessage`; a card's are
 * unprefixed. The chat UI treats a proposal the same either way, so both names count.
 */
export const isCreateChatMessageTool = (name) => name === 'createChatMessage' || name === 'createBoardChatMessage';

export const isUpdateChatMessageTool = (name) => name === 'updateChatMessage' || name === 'updateBoardChatMessage';

const _toolCallContainerId = (toolCall) => toolCall.input?.cardId || toolCall.input?.boardId;

/** The first tool call still waiting for approval that `isMatch` accepts, with its doc's `responseId`; null when none is. */
const _firstWaitingToolCall = (toolMessageDocs, resolved, isMatch) => {
  for (const doc of toolMessageDocs) {
    if (!Array.isArray(doc.toolCalls)) continue;
    for (const tc of doc.toolCalls) {
      if (isAwaitingApproval(tc, resolved) && isMatch(tc)) return { ...tc, responseId: doc.responseId };
    }
  }
  return null;
};

/**
 * The waiting `createChatMessage` proposal for this chat — a message the AI would post, shown in the
 * chat box rather than as a row in the thread: it is still a message the user is about to send, and
 * the box is where they decide about it and correct it.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent AI chat id
 * @param {String} containerId - Card id for a card chat, board id for a board chat
 * @returns {Object|null} The tool call (with `responseId`), or null when there is no such proposal
 */
export const createChatMessageProposal = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, containerId) => containerId,
  (state) => resolvedToolCalls(state),
  (toolMessageDocs, containerId, resolved) => {
    if (!toolMessageDocs || !containerId) return null;
    return _firstWaitingToolCall(toolMessageDocs, resolved, (tc) =>
      isCreateChatMessageTool(tc.name) && _toolCallContainerId(tc) === containerId);
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * What a proposal will actually do: the user's own version when they have corrected it, and the
 * assistant's otherwise.
 *
 * Every component that renders a proposal reads it through this, so none of them has to know whether
 * it was corrected. The correction lives on the tool call as `editedInput`, put there by
 * `updateToolCallInput`, so it survives a refresh without anything holding it here.
 *
 * @param {Object} toolCall - The tool call
 * @returns {Object} The input to render and to act on
 */
export const toolCallInput = (toolCall) => (toolCall && (toolCall.editedInput || toolCall.input)) || {};

/**
 * The waiting `updateChatMessage` proposal for one posted message — shown as that message's own diff
 * row in the thread, and taken into its editor when the user presses Edit.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent AI chat id
 * @param {String} chatMessageId - The posted message
 * @returns {Object|null} The tool call (with `responseId`), or null when none is waiting on it
 */
export const updateChatMessageProposal = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, chatMessageId) => chatMessageId,
  (state) => resolvedToolCalls(state),
  (toolMessageDocs, chatMessageId, resolved) => {
    if (!toolMessageDocs || !chatMessageId) return null;
    return _firstWaitingToolCall(toolMessageDocs, resolved, (tc) =>
      isUpdateChatMessageTool(tc.name) && tc.input?.chatMessageId === chatMessageId);
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * True when the only things waiting on this card or board are chat proposals — a message being
 * written or one being rewritten. Both are shown in an editor for the user to correct, so locking the
 * thread would take away the point of them. Anything else waiting alongside still locks it.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Parent AI chat id
 * @param {String} containerId - Card id for a card chat, board id for a board chat
 * @returns {Boolean}
 */
export const isOnlyChatProposalsPending = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, containerId) => containerId,
  (state) => resolvedToolCalls(state),
  (toolMessageDocs, containerId, resolved) => {
    if (!toolMessageDocs || !containerId) return false;
    const isHere = (tc) => _toolCallContainerId(tc) === containerId;
    const isChatTool = (tc) => isCreateChatMessageTool(tc.name) || isUpdateChatMessageTool(tc.name);
    if (_firstWaitingToolCall(toolMessageDocs, resolved, (tc) => isHere(tc) && !isChatTool(tc))) return false;
    return !!_firstWaitingToolCall(toolMessageDocs, resolved, isHere);
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * Returns the AI suggestion type for a card: 'NEW' | 'EDIT' | 'DELETE' | null.
 *
 * - 'NEW'    → createCard ToolCall with input.cardId === cardId in WAITING_FOR_APPROVAL
 * - 'DELETE' → deleteCard ToolCall with input.cardId === cardId in WAITING_FOR_APPROVAL
 * - 'EDIT'   → any leaf edit ToolCall with input.cardId === cardId in WAITING_FOR_APPROVAL
 * - null     → no pending AI suggestion
 *
 * Priority when multiple types exist: DELETE > EDIT > NEW.
 *
 * Used by kerika-board-card to render the appropriate dashed border + badge and to
 * suppress mutating interactions while a suggestion is pending.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} cardId - Card ID
 * @returns {'NEW'|'EDIT'|'DELETE'|null}
 */
export const cardSuggestionType = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, cardId) => cardId,
  (state) => toolsConfig(state),
  (state) => resolvedToolCalls(state),
  (toolMessageDocs, cardId, toolsConf, resolved) => {
    if (!toolMessageDocs || !cardId) return null;
    const leafToolNames = new Set(
      Object.values(toolsConf).flatMap(c => (c.leafTools ? c.leafTools : []))
    );
    let result = null;
    for (const doc of toolMessageDocs) {
      if (!doc.toolCalls) continue;
      for (const tc of doc.toolCalls) {
        if (!isAwaitingApproval(tc, resolved) || tc.input?.cardId !== cardId) continue;
        if (tc.name === 'deleteCard') return 'DELETE';
        if (leafToolNames.has(tc.name)) result = 'EDIT';
        else if (tc.name === 'createCard' && result === null) result = 'NEW';
      }
    }
    return result;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual, maxSize: 200 } }
);

/**
 * True only when every pending ToolCall on the card is a `moveCard` (a column move) —
 * i.e. the card's suggestion is exclusively a reorder. A move bundled with any other
 * change is a normal edit. Used by the board card to read "Reorder" instead of "Edit".
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} cardId - Card ID
 * @returns {Boolean}
 */
export const cardMoveOnly = createSelector(
  (state, chatId) => allToolMessages(state, chatId),
  (state, chatId, cardId) => cardId,
  (state) => resolvedToolCalls(state),
  (toolMessageDocs, cardId, resolved) => {
    if (!toolMessageDocs || !cardId) return false;
    let sawMove = false;
    for (const doc of toolMessageDocs) {
      if (!doc.toolCalls) continue;
      for (const tc of doc.toolCalls) {
        if (!isAwaitingApproval(tc, resolved) || tc.input?.cardId !== cardId) continue;
        if (tc.name !== 'moveCard') return false;
        sawMove = true;
      }
    }
    return sawMove;
  },
  { memoizeOptions: { maxSize: 200 } }
);

// ============================================================================
// Quick Actions Selectors
// ============================================================================

/**
 * Returns quick actions grouped by groupName.
 *
 * Reads from Firestore queries for default quick actions (contextTypes == null or [])
 * and context-specific quick actions (contextTypes array-contains contextType).
 * Combines, filters, sorts by order, and groups by groupName. The
 * MAKE_BOARD_LIKE_THIS ("Make a Copy of this Board") action is included only on
 * public / use-case boards (never on templates).
 *
 * @param {Object} state - Redux state
 * @param {String} contextType - Context type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * @returns {Object} Object with group names as keys and arrays of quick actions as values
 */
export const quickActions = createSelector(
  (state) => defaultQuickActions(state),
  (state, contextType) => firestoreRedux.selectors.docsByQueryResult(state, `ai-quick-actions-${contextType}`, 'ai-quick-actions'),
  (state) => board.selectors.makeCopyBoardFeatureAvailable(state),
  (state) => board.selectors.isTemplate(state),
  (defaultActions, contextActions, canMakeCopyBoard, isTemplate) => {
    // Combine default and context-specific quick actions
    const allItems = [...(defaultActions || []), ...(contextActions || [])];

    if (allItems.length === 0) return {};

    // Remove duplicates by id
    const uniqueItemsMap = new Map();
    allItems.forEach(item => {
      if (!uniqueItemsMap.has(item.id)) {
        uniqueItemsMap.set(item.id, item);
      }
    });
    const uniqueItems = Array.from(uniqueItemsMap.values());

    // Filter out disabled items, and those whose `filter` keeps them out of the AI Helper (button-only ones)
    const enabled = uniqueItems.filter((item) => !item.disabled && isQuickActionAllowed(item, { trigger: 'LIST' }));

    // "Make a Copy of this Board" (MAKE_BOARD_LIKE_THIS) is offered only on public /
    // use-case boards, never on templates - mirrors the board header's "Make a Copy"
    // gating (board.selectors.makeCopyBoardFeatureAvailable).
    const visible = (canMakeCopyBoard && !isTemplate)
      ? enabled
      : enabled.filter((item) => item.name !== 'MAKE_BOARD_LIKE_THIS');

    // Sort by order
    const sorted = visible.sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    // Group by groupName, using 'Other' for items without a groupName
    const grouped = groupBy(sorted, (action) => action.groupName || 'Other');

    // Reorder so that named groups come first and 'Other' (default) comes last
    return { ...omit(grouped, 'Other'), ...(grouped['Other'] ? { Other: grouped['Other'] } : {}) };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10
    }
  }
);

/**
 * Returns whether quick actions are available for the given context.
 *
 * @param {Object} state - Redux state
 * @param {String} contextType - Context type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * @returns {Boolean} true if quick actions are available
 */
export const hasQuickActions = (state, contextType) => {
  const actions = quickActions(state, contextType);
  return actions && Object.keys(actions).length > 0;
};

/**
 * Returns tool messages that belong to the chat's last (latest) message.
 *
 * Filters `allToolMessages` by matching `messageId` to the last message's ID.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {Array} Tool message docs whose messageId matches the last message
 */
export const latestMessageTools = createSelector(
  (state, chatId) => lastMessage(state, chatId),
  (state, chatId) => allToolMessages(state, chatId),
  (lastMsg, toolMsgs) => {
    if (!lastMsg) return [];
    return toolMsgs.filter(tm => tm.messageId === lastMsg.id);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);


/**
 * Returns whether a board creation has already been auto-navigated.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @param {String} toolMessageId - Tool message document ID
 * @returns {Boolean} true if this board creation was already processed
 */
export const isBoardCreationDone = (state, chatId, toolMessageId) => {
  return !!get(state, `ai-chat.completedBoardCreations.${chatId}_${toolMessageId}`);
};


/**
 * Returns whether auto-submit is pending for a given context.
 * True when the user clicked Send while uploads were in progress.
 *
 * @param {Object} state - Redux state
 * @param {String} contextKey - e.g. 'board_brd_123', 'aic_456', 'home'
 * @returns {Boolean}
 */
export const autoSubmitPending = (state, contextKey) => {
  return !!(contextKey && get(state, `ai-chat.autoSubmitPending.${contextKey}`, false));
}

// ============================================================================
// Sharing Selectors
// ============================================================================

/**
 * Returns whether the chat can be shared.
 *
 * A chat is shareable when it has at least one message with a successfully
 * completed response (status: COMPLETED or ERROR).
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {Boolean} true if chat has at least one completed response
 */
export const canShareChat = (state, chatId) => {
  if (!chatId) return false;
  const msgs = messages(state, chatId);
  if (!msgs || msgs.length === 0) return false;
  return msgs.some(msg => {
    if (!msg.responses || msg.responses.length === 0) return false;
    return msg.responses.some(r => r.status === 'COMPLETED' || r.status === 'ERROR');
  });
};

/**
 * Returns whether the chat is shared.
 *
 * Reads the `shared` field from the Firestore chat document.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {Boolean} true if the chat is shared
 */
export const isChatShared = (state, chatId) => {
  return get(chat(state, chatId), 'shared', false);
};

/**
 * Returns the userId of the user who shared the chat.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {String|null} userId of the user who shared the chat, or null
 */
export const chatSharedByUserId = (state, chatId) => {
  return get(chat(state, chatId), 'userId', null);
};

/**
 * Returns the first name of the user who shared the chat.
 *
 * @param {Object} state - Redux state
 * @param {String} chatId - Chat ID
 * @returns {String|null} firstName of the user who shared the chat, or null
 */
export const chatSharedByName = (state, chatId) => {
  const userId = chatSharedByUserId(state, chatId);
  if (!userId) return null;
  return user.selectors.attrs(state, userId)?.firstName || null;
};
